// mobile/screens/HarvestsScreen.js
import React, { useEffect, useRef, useState, useMemo } from 'react';
import {
  View, Text, StyleSheet, ScrollView, RefreshControl, TouchableOpacity,
  ActivityIndicator, Alert, AppState, Platform, Modal, TextInput, Linking,
  Image, KeyboardAvoidingView
} from 'react-native';
import axios from 'axios';
import { API_BASE } from '../utils';
import { useFocusEffect } from '@react-navigation/native';
import * as ImagePicker from 'expo-image-picker';
import EnterpriseHeader from '../components/EnterpriseHeader';
import PadiProIcon from '../components/PadiProIcon';
import { PADIPRO, padiproShadow } from '../components/padiproTheme';
import { useResponsive } from '../components/responsive';
// Native date picker (Android/iOS)
import DateTimePicker from '@react-native-community/datetimepicker';
// JS-only date picker (works on Web)
import DatePicker from 'react-native-ui-datepicker';
import dayjs from 'dayjs';

const isWeb = Platform.OS === 'web';
const AUTO_REFRESH_MS = 15000;
const DEFAULT_SACKS = '1';

export default function HarvestsScreen({ route }) {
  const token = route?.params?.token;

  const { contentWidthStyle, isWide } = useResponsive();

  // data
  const [fields, setFields]   = useState([]);
  const [items, setItems]     = useState([]);
  const [seasonHarvestItems, setSeasonHarvestItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [fieldPlots, setFieldPlots] = useState([]);

  // ui states
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [errorText, setErrorText] = useState('');

  // filter & sort
  const [seasons, setSeasons] = useState([]);
  const [seasonLoading, setSeasonLoading] = useState(false);
  const [selectedSeasonId, setSelectedSeasonId] = useState(null);
  const [sortKey, setSortKey] = useState('date'); // 'date' | 'yield' | 'revenue'
  const [sortDir, setSortDir] = useState('desc'); // 'asc' | 'desc'
  const [query, setQuery] = useState('');

  // compact filter UI
  const [showFilters, setShowFilters] = useState(false);

  // add (manual)
  const [showAdd, setShowAdd] = useState(false);
  const [addFieldId, setAddFieldId] = useState(null);
  const [addPlotId, setAddPlotId] = useState(null);
  const [addDate, setAddDate] = useState(() => new Date().toISOString().slice(0,10));
  const [addKg, setAddKg] = useState('');
  const [addPrice, setAddPrice] = useState('');
  const [addBuyer, setAddBuyer] = useState('');
  const [addEvidenceAsset, setAddEvidenceAsset] = useState(null);
  const [savingAdd, setSavingAdd] = useState(false);

  // kalkulator timbangan (NET KG per timbang)
  const [showCalc, setShowCalc] = useState(false);
  const [calcFieldId, setCalcFieldId] = useState(null);
  const [calcPlotId, setCalcPlotId] = useState(null);
  const [calcDate, setCalcDate] = useState(() => new Date().toISOString().slice(0,10));
  const [calcPrice, setCalcPrice] = useState('');
  const [calcKg, setCalcKg] = useState('');
  const [calcSacks, setCalcSacks] = useState(DEFAULT_SACKS);
  const [calcRows, setCalcRows]   = useState([]);
  const [savingCalc, setSavingCalc] = useState(false);
  const [notesCalc, setNotesCalc] = useState('');
  const [buyerCalc, setBuyerCalc] = useState('');
  const [calcEvidenceAsset, setCalcEvidenceAsset] = useState(null);
  const calcKgInputRef = useRef(null);
  const calcSacksInputRef = useRef(null);
  const [showCalcDatePicker, setShowCalcDatePicker] = useState(false);

  // view weighings
  const [viewWeighModal, setViewWeighModal] = useState(false);
  const [viewWeighRows, setViewWeighRows] = useState([]);
  const [viewWeighLoading, setViewWeighLoading] = useState(false);
  const [viewWeighHarvestId, setViewWeighHarvestId] = useState(null);

  // edit price per harvest
  const [editPriceModal, setEditPriceModal] = useState(false);
  const [editPriceHarvest, setEditPriceHarvest] = useState(null); // harvest object
  const [editPriceValue, setEditPriceValue] = useState('');
  const [savingPrice, setSavingPrice] = useState(false);

  // payment / DP from buyer (toke or tengkulak)
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [paymentHarvest, setPaymentHarvest] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState('0');
  const [paymentNote, setPaymentNote] = useState('');
  const [paymentUpdatingId, setPaymentUpdatingId] = useState(null);
  const [paymentRows, setPaymentRows] = useState([]);
  const [paymentRowsLoading, setPaymentRowsLoading] = useState(false);
  const [paymentDate, setPaymentDate] = useState(() => new Date().toISOString().slice(0,10));
  const [paymentMethod, setPaymentMethod] = useState('transfer');
  const [editPaymentId, setEditPaymentId] = useState(null);

  // direct harvest cost: Odong-odong / Combine Harvester
  const [combineModalOpen, setCombineModalOpen] = useState(false);
  const [combineHarvest, setCombineHarvest] = useState(null);
  const [combineCostValue, setCombineCostValue] = useState('0');
  const [combineOperator, setCombineOperator] = useState('');
  const [combineNote, setCombineNote] = useState('');
  const [combineUpdatingId, setCombineUpdatingId] = useState(null);

  // receipt image viewer modal
  const [receiptViewerOpen, setReceiptViewerOpen] = useState(false);
  const [receiptViewerUrl, setReceiptViewerUrl] = useState(null);
  const [evidenceUploadId, setEvidenceUploadId] = useState(null);
  const [evidenceNotice, setEvidenceNotice] = useState(null);
  const [showAddPlot, setShowAddPlot] = useState(false);
  const [plotTarget, setPlotTarget] = useState('add');
  const [newPlotFieldId, setNewPlotFieldId] = useState(null);
  const [newPlotName, setNewPlotName] = useState('');
  const [savingPlot, setSavingPlot] = useState(false);

  // infra
  const authHdr = { Authorization: `Bearer ${token}` };
  const intervalRef = useRef(null);
  const appState = useRef(AppState.currentState);
  const mountedRef = useRef(true);
  const selectedSeasonIdRef = useRef(null);
  const fetchSequenceRef = useRef(0);

  // ===== Helpers =====
  const formatIDR = (n) => `Rp ${Number(n || 0).toLocaleString('id-ID')}`;
  const parseInputNumber = (value, { allowDecimal = true, defaultValue = 0 } = {}) => {
    const raw = String(value ?? '').trim();
    if (!raw) return defaultValue;

    if (allowDecimal) {
      let normalized = raw.replace(/\s+/g, '');
      if (normalized.includes(',') && normalized.includes('.')) {
        normalized = normalized.lastIndexOf(',') > normalized.lastIndexOf('.')
          ? normalized.replace(/\./g, '').replace(',', '.')
          : normalized.replace(/,/g, '');
      } else if (normalized.includes(',')) {
        normalized = normalized.replace(/\./g, '').replace(',', '.');
      } else {
        normalized = normalized.replace(/,/g, '');
      }
      const n = Number(normalized);
      return Number.isFinite(n) ? n : defaultValue;
    }

    const normalizedInt = raw.replace(/[^\d-]/g, '');
    const n = parseInt(normalizedInt || '0', 10);
    return Number.isFinite(n) ? n : defaultValue;
  };
  const normalizeFieldId = (f) => {
    const id = f?.id ?? f?.field_id;
    if (id == null || id === '') return null;
    return String(id);
  };
  const toTon = (kg) => (Number(kg || 0) / 1000);
  const toYMD = (d) => {
    const dt = d ? new Date(d) : new Date();
    if (Number.isNaN(dt.getTime())) return null;
    const y = dt.getFullYear();
    const m = String(dt.getMonth()+1).padStart(2,'0');
    const dd= String(dt.getDate()).padStart(2,'0');
    return `${y}-${m}-${dd}`;
  };
  const fmtDateShort = (d) => {
    if (!d) return '—';
    const dt = (d instanceof Date) ? d : new Date(d);
    return dt.toLocaleDateString('id-ID', { day:'2-digit', month:'short' });
  };
  const fmtDateLong = (d) => {
    if (!d) return '—';
    const dt = (d instanceof Date) ? d : new Date(d);
    if (Number.isNaN(dt.getTime())) return '—';
    return dt.toLocaleDateString('id-ID', { day:'2-digit', month:'short', year:'numeric' });
  };
  const getSeasonName = (season) => {
    if (!season) return 'Musim';
    const name = String(season.name || '').trim();
    return name || `Musim ${season.season_id}`;
  };
  const getSeasonRangeLabel = (season) => {
    if (!season?.start_date) return 'Rentang tanggal belum tersedia';
    const start = fmtDateLong(season.start_date);
    const end = season.end_date ? fmtDateLong(season.end_date) : 'Berjalan';
    return `${start} - ${end}`;
  };

  const selectedSeason = useMemo(
    () => seasons.find((x) => String(x.season_id) === String(selectedSeasonId)) || null,
    [seasons, selectedSeasonId]
  );
  const activeSeason = useMemo(
    () => seasons.find((x) => x && x.end_date == null) || null,
    [seasons]
  );

  const buildQuery = (seasonIdOverride = null) => {
    const q = new URLSearchParams();
    const sid = seasonIdOverride ?? selectedSeasonIdRef.current ?? selectedSeasonId;
    if (sid) q.append('season_id', String(sid));
    q.append('sort', `${sortKey}:${sortDir}`);
    return q.toString();
  };

  const selectSeason = (seasonId) => {
    const normalized = String(seasonId || '').trim() || null;
    selectedSeasonIdRef.current = normalized;
    setSelectedSeasonId(normalized);
  };

  const fetchSeasons = async ({ silent = false } = {}) => {
    try {
      if (!silent) setSeasonLoading(true);
      const r = await axios.get(`${API_BASE}/seasons`, { headers: authHdr });
      if (!mountedRef.current) return null;

      const list = Array.isArray(r.data) ? r.data : [];
      setSeasons(list);

      const active = list.find((s) => s && s.end_date == null);
      const nextSeasonId = String(active?.season_id || list?.[0]?.season_id || '');
      const currentSeasonId = selectedSeasonIdRef.current;
      const hasSelected = currentSeasonId && list.some((s) => String(s.season_id) === String(currentSeasonId));

      if (!hasSelected) {
        selectSeason(nextSeasonId);
        return nextSeasonId || null;
      }
      return currentSeasonId;
    } catch (e) {
      if (!mountedRef.current) return null;
      const msg = e?.response?.data?.error || e?.message || 'Gagal memuat musim';
      if (Platform.OS === 'web') console.error(msg);
      return null;
    } finally {
      if (mountedRef.current) setSeasonLoading(false);
    }
  };

  const fetchAll = async ({ silent=false, seasonIdOverride = null } = {}) => {
    const controller = new AbortController();
    const requestSequence = ++fetchSequenceRef.current;
    try {
      if (!silent) setLoading(true);
      setErrorText('');

      const sid = String(
        seasonIdOverride ?? selectedSeasonIdRef.current ?? selectedSeasonId ?? ''
      ).trim();
      if (!sid) {
        if (mountedRef.current && requestSequence === fetchSequenceRef.current) {
          setFields([]);
          setItems([]);
          setSeasonHarvestItems([]);
          setSummary({ total_kg: 0, total_revenue: 0, avg_price: 0, total_fields_harvested: 0 });
        }
        return;
      }

      const queryString = buildQuery(sid);
      const fieldsQs = new URLSearchParams();
      fieldsQs.append('season_id', sid);
      const fieldsSummaryUrl = `${API_BASE}/operations/fields-summary?${fieldsQs.toString()}`;

      const [fs, hs, ps] = await Promise.all([
        axios.get(fieldsSummaryUrl, { headers: authHdr, signal: controller.signal }),
        axios.get(`${API_BASE}/harvests?${queryString}`,   { headers: authHdr, signal: controller.signal }),
        axios.get(`${API_BASE}/harvests/field-plots`, { headers: authHdr, signal: controller.signal }),
      ]);

      if (!mountedRef.current || requestSequence !== fetchSequenceRef.current) return;

      const normalizedFields = (Array.isArray(fs.data?.fields) ? fs.data.fields : [])
        .map((f) => {
          const id = normalizeFieldId(f);
          if (!id) return null;
          return { ...f, id };
        })
        .filter(Boolean);
      setFields(normalizedFields);
      setFieldPlots(Array.isArray(ps.data?.items) ? ps.data.items : []);

      const seasonItems = (Array.isArray(hs.data?.items) ? hs.data.items : [])
        .filter((row) => String(row?.season_id || '').trim() === sid);
      setSeasonHarvestItems(seasonItems);
      let it = seasonItems;
      if (query.trim()) {
        const q = query.trim().toLowerCase();
        const idToName = Object.fromEntries(normalizedFields.map((f) => [String(f.id), String(f.name || '').toLowerCase()]));
        it = it.filter(r =>
          (idToName[String(r.field_id)] || '').includes(q)
          || String(r.plot_name || '').toLowerCase().includes(q)
          || String(r.kg||'').includes(q)
          || String(r.price_per_kg||'').includes(q)
        );
      }
      setItems(it);

      // Hitung dari baris yang sudah lolos verifikasi season_id. Dengan begitu
      // respons lama/stale atau agregat backend yang salah tidak bocor ke kartu UI.
      setSummary({
        total_kg: seasonItems.reduce((a,b)=>a+Number(b.kg||0),0),
        total_revenue: seasonItems.reduce((a,b)=>a+Number(b.total ?? (Number(b.kg||0)*Number(b.price_per_kg||0))),0),
        avg_price: (() => {
          const wsum = seasonItems.reduce((a,b)=>a + Number(b.kg||0)*Number(b.price_per_kg||0),0);
          const kg   = seasonItems.reduce((a,b)=>a + Number(b.kg||0),0);
          return kg>0 ? (wsum/kg) : 0;
        })(),
        total_fields_harvested: new Set(seasonItems.map(x=>x.field_id)).size,
      });
    } catch (e) {
      if (!mountedRef.current || requestSequence !== fetchSequenceRef.current) return;
      const msg = e?.response?.data?.error || e?.message || 'Gagal memuat hasil panen';
      setErrorText(msg);
      if (Platform.OS === 'web') console.error(msg);
      else Alert.alert('Error', msg);
    } finally {
      if (!mountedRef.current || requestSequence !== fetchSequenceRef.current) return;
      setLoading(false);
      setRefreshing(false);
    }
    return () => controller.abort();
  };

  // lifecycle
  useEffect(() => {
    mountedRef.current = true;
    (async () => {
      const sid = await fetchSeasons();
      await fetchAll({ seasonIdOverride: sid });
    })();
    return () => { mountedRef.current = false; };
  }, []);

  useEffect(() => {
    intervalRef.current = setInterval(() => fetchAll({ silent: true }), AUTO_REFRESH_MS);
    return () => clearInterval(intervalRef.current);
  }, [selectedSeasonId, sortKey, sortDir]);

  useFocusEffect(
    React.useCallback(() => {
      let cancelled = false;
      (async () => {
        const sid = await fetchSeasons({ silent: true });
        if (cancelled) return;
        await fetchAll({ silent: true, seasonIdOverride: sid });
      })();
      return () => { cancelled = true; };
    }, [selectedSeasonId, sortKey, sortDir])
  );

  useEffect(() => {
    const sub = AppState.addEventListener('change', (next) => {
      const prev = appState.current; appState.current = next;
      if (prev?.match(/inactive|background/) && next === 'active') fetchAll({silent:true});
    });
    return () => sub.remove();
  }, [selectedSeasonId, sortKey, sortDir]);
  useEffect(() => { fetchAll({ silent: true }); }, [selectedSeasonId, sortKey, sortDir]);
  useEffect(() => {
    if (!fields.length) {
      setAddFieldId(null);
      setCalcFieldId(null);
      return;
    }

    const ids = new Set(fields.map((f) => String(f.id)));
    const fallback = String(fields[0].id);

    if (!addFieldId || !ids.has(String(addFieldId))) setAddFieldId(fallback);
    if (!calcFieldId || !ids.has(String(calcFieldId))) setCalcFieldId(fallback);
  }, [fields]);
  useEffect(() => {
    const plotMatchesField = (plotId, fieldId) => fieldPlots.some(
      (plot) => String(plot.plot_id) === String(plotId) && String(plot.field_id) === String(fieldId)
    );
    if (addPlotId && !plotMatchesField(addPlotId, addFieldId)) setAddPlotId(null);
    if (calcPlotId && !plotMatchesField(calcPlotId, calcFieldId)) setCalcPlotId(null);
  }, [fieldPlots, addFieldId, addPlotId, calcFieldId, calcPlotId]);

  const onRefresh = async () => {
    setRefreshing(true);
    const sid = await fetchSeasons({ silent: true });
    await fetchAll({ seasonIdOverride: sid });
  };

  // add handler (manual)
  const onSaveAdd = async () => {
    if (!addFieldId) { Alert.alert('Validasi','Pilih lahan.'); return; }
    const fieldNum = Number(addFieldId);
    if (!Number.isFinite(fieldNum) || fieldNum <= 0) { Alert.alert('Validasi', 'Lahan tidak valid.'); return; }
    const kg = parseInputNumber(addKg, { allowDecimal: true, defaultValue: 0 });
    const price = parseInputNumber(addPrice, { allowDecimal: false, defaultValue: 0 });
    if (!(kg > 0)) { Alert.alert('Validasi','Masukkan hasil (kg) > 0.'); return; }
    if (!(price >= 0)) { Alert.alert('Validasi','Harga/kg tidak valid.'); return; }

    try {
      setSavingAdd(true);
      const hadEvidencePhoto = !!addEvidenceAsset?.uri;
      const response = await axios.post(`${API_BASE}/harvests`, {
        field_id: fieldNum,
        plot_id: addPlotId ? Number(addPlotId) : null,
        date: addDate || toYMD(new Date()),
        kg,
        price_per_kg: price,
        buyer: addBuyer.trim() || null,
      }, { headers: authHdr });

      let photoError = null;
      let uploadedEvidenceUrl = null;
      const harvestId = response?.data?.id;
      if (addEvidenceAsset?.uri && harvestId) {
        try {
          const uploadResponse = await uploadEvidenceAsset(harvestId, addEvidenceAsset);
          uploadedEvidenceUrl = normalizeUrl(uploadResponse?.data?.evidence_url);
        } catch (uploadError) {
          photoError = uploadError?.response?.data?.error || uploadError?.message || 'Upload foto bon gagal';
        }
      }

      setShowAdd(false);
      setAddKg('');
      setAddPrice('');
      setAddBuyer('');
      setAddEvidenceAsset(null);
      await fetchAll({ silent: true });
      if (hadEvidencePhoto && !photoError && uploadedEvidenceUrl) {
        setEvidenceNotice({
          title: 'Bon fisik berhasil disimpan',
          detail: 'Lampiran sudah terhubung ke hasil panen dan siap dilihat.',
          url: uploadedEvidenceUrl,
        });
      }
      Alert.alert(
        photoError ? 'Panen Tersimpan' : 'Sukses',
        photoError
          ? `Hasil panen sudah tersimpan, tetapi foto bon gagal diunggah: ${photoError}. Foto masih dapat ditambahkan dari card panen.`
          : hadEvidencePhoto
            ? 'Hasil panen dan foto bon berhasil disimpan.'
            : 'Hasil panen berhasil disimpan.'
      );
    } catch (e) {
      const msg = e?.response?.data?.error || e?.message || 'Gagal menyimpan';
      if (Platform.OS === 'web') console.error(msg);
      Alert.alert('Error', msg);
    } finally {
      setSavingAdd(false);
    }
  };

  // export laporan periode
  const onExportPDF = () => {
    const url = `${API_BASE}/harvests/report.pdf?${buildQuery()}&bearer=${encodeURIComponent(token)}`;
    if (Platform.OS === 'web') window.open(url, '_blank');
    else Linking.openURL(url).catch(() => Alert.alert('Error', 'Gagal membuka PDF'));
  };

  // list helpers
  const idToName = useMemo(
    () => Object.fromEntries(fields.map((f) => [String(f.id), f.name])),
    [fields]
  );

  const plotBreakdown = useMemo(() => {
    const grouped = new Map();
    for (const row of seasonHarvestItems) {
      const fieldId = String(row.field_id || '');
      if (!fieldId) continue;
      if (!grouped.has(fieldId)) {
        grouped.set(fieldId, {
          field_id: fieldId,
          field_name: idToName[fieldId] || 'Lahan',
          entries: new Map(),
        });
      }
      const group = grouped.get(fieldId);
      const hasPlot = row.plot_id != null && String(row.plot_id).trim() !== '';
      const key = hasPlot ? `plot:${row.plot_id}` : 'unassigned';
      if (!group.entries.has(key)) {
        group.entries.set(key, {
          key,
          plot_id: hasPlot ? row.plot_id : null,
          name: hasPlot ? (String(row.plot_name || '').trim() || `Petakan #${row.plot_id}`) : 'Tanpa petakan / gabungan',
          kg: 0,
          revenue: 0,
          harvest_count: 0,
        });
      }
      const entry = group.entries.get(key);
      entry.kg += Number(row.kg || 0);
      entry.revenue += Number(row.total ?? (Number(row.kg || 0) * Number(row.price_per_kg || 0)));
      entry.harvest_count += 1;
    }

    return Array.from(grouped.values())
      .map((group) => {
        const entries = Array.from(group.entries.values()).sort((a, b) => b.kg - a.kg || a.name.localeCompare(b.name, 'id'));
        const totalKg = entries.reduce((sum, entry) => sum + entry.kg, 0);
        return {
          ...group,
          total_kg: totalKg,
          entries: entries.map((entry, index) => ({
            ...entry,
            rank: index + 1,
            share: totalKg > 0 ? entry.kg / totalKg : 0,
          })),
          has_named_plot: entries.some((entry) => entry.plot_id != null),
        };
      })
      .filter((group) => group.has_named_plot)
      .sort((a, b) => a.field_name.localeCompare(b.field_name, 'id'));
  }, [seasonHarvestItems, idToName]);

  const paymentSummary = useMemo(() => {
    return seasonHarvestItems.reduce((acc, row) => {
      const gross = Number(row.total ?? (Number(row.kg || 0) * Number(row.price_per_kg || 0)));
      const combineCost = Math.max(0, Number(row.combine_cost || 0));
      const total = Math.max(0, gross - combineCost);
      const rawReceived = Math.max(0, Number(row.down_payment_amount || 0));
      const received = Math.min(total, rawReceived);
      const pending = Math.max(0, total - received);
      acc.received += received;
      acc.pending += pending;
      if (pending <= 0) acc.paidCount += 1;
      else {
        acc.pendingCount += 1;
        if (received > 0) acc.partialCount += 1;
      }
      return acc;
    }, { received: 0, pending: 0, paidCount: 0, partialCount: 0, pendingCount: 0 });
  }, [seasonHarvestItems]);

  const combineSummary = useMemo(() => {
    return seasonHarvestItems.reduce((acc, row) => {
      const gross = Number(row.total ?? (Number(row.kg || 0) * Number(row.price_per_kg || 0)));
      const cost = Math.max(0, Number(row.combine_cost || 0));
      acc.gross += gross;
      acc.cost += cost;
      acc.net += gross - cost;
      if (cost > 0) acc.count += 1;
      return acc;
    }, { gross: 0, cost: 0, net: 0, count: 0 });
  }, [seasonHarvestItems]);

  const openBon = (url) => Linking.openURL(url).catch(()=>Alert.alert('Error','Gagal membuka bon'));

  const normalizeUrl = (u) => {
    const s = String(u || '');
    if (!s) return null;
    if (/^https?:\/\//i.test(s)) return s;
    return `${API_BASE}${s}`;
  };

  const isLikelyImageUrl = (u) => {
    const s = String(u || '').toLowerCase();
    return /\.(jpg|jpeg|png|webp)$/i.test(s) || s.includes('image') || s.includes('receipt') || s.includes('photo');
  };

  const openReceiptViewer = (url) => {
    if (!url) return;
    setReceiptViewerUrl(url);
    setReceiptViewerOpen(true);
  };

  const pickEvidenceAsset = async () => {
    const { status } = await ImagePicker.requestMediaLibraryPermissionsAsync();
    if (status !== 'granted') {
      Alert.alert('Izin ditolak', 'Butuh akses galeri untuk memilih foto bon.');
      return null;
    }

    const pick = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ImagePicker.MediaTypeOptions.Images,
      allowsEditing: false,
      quality: 0.85,
    });
    if (pick.canceled) return null;
    const asset = pick.assets?.[0];
    if (!asset?.uri) return null;
    return asset;
  };

  const uploadEvidenceAsset = async (hid, asset) => {
    if (!hid || !asset?.uri) return null;
    const form = new FormData();
    if (Platform.OS === 'web') {
      const resp = await fetch(asset.uri);
      const blob = await resp.blob();
      const ext = (blob.type?.split('/')?.[1] || 'jpg').toLowerCase();
      form.append('photo', blob, asset.fileName || `bon-panen-${hid}.${ext}`);
    } else {
      form.append('photo', {
        uri: asset.uri,
        name: asset.fileName || `bon-panen-${hid}.jpg`,
        type: asset.mimeType || 'image/jpeg',
      });
    }
    return axios.post(`${API_BASE}/harvests/${hid}/evidence`, form, { headers: { ...authHdr } });
  };

  const uploadEvidenceForHarvest = async (hid) => {
    try {
      const asset = await pickEvidenceAsset();
      if (!asset) return;
      setEvidenceUploadId(String(hid));
      const response = await uploadEvidenceAsset(hid, asset);
      const attachmentUrl = normalizeUrl(response?.data?.evidence_url);
      await fetchAll({ silent: true });
      setEvidenceNotice({
        title: 'Bon fisik berhasil disimpan',
        detail: 'Lampiran sudah terhubung ke hasil panen dan siap dilihat.',
        url: attachmentUrl,
      });
      Alert.alert('Sukses', 'Foto bon berhasil diunggah.');
    } catch (e) {
      const msg = e?.response?.data?.error || e?.message || 'Gagal upload foto bon';
      if (Platform.OS === 'web') console.error(e);
      Alert.alert('Error', msg);
    } finally {
      setEvidenceUploadId(null);
    }
  };

  const EvidencePicker = ({ asset, onChange }) => (
    <View style={s.evidencePickerBox}>
      <View style={s.evidencePickerHead}>
        <View style={{ flex: 1 }}>
          <Text style={s.evidencePickerTitle}>Foto Bon Tengkulak / Pembeli</Text>
          <Text style={s.evidencePickerHint}>Bon fisik sebagai evidence · JPG, PNG, WEBP, atau HEIC</Text>
        </View>
        <TouchableOpacity
          style={s.evidencePickerBtn}
          onPress={async () => {
            const picked = await pickEvidenceAsset();
            if (picked) onChange(picked);
          }}
          activeOpacity={0.85}
        >
          <Text style={s.evidencePickerBtnText}>{asset ? 'Ganti Foto' : 'Pilih Foto'}</Text>
        </TouchableOpacity>
      </View>
      {asset?.uri && (
        <View style={s.evidencePreviewRow}>
          <Image source={{ uri: asset.uri }} style={s.evidencePreviewImage} resizeMode="cover" />
          <View style={{ flex: 1 }}>
            <Text style={s.evidencePreviewName} numberOfLines={1}>{asset.fileName || 'Foto bon dipilih'}</Text>
            <Text style={s.evidencePreviewReady}>Siap diunggah saat panen disimpan</Text>
          </View>
          <TouchableOpacity style={s.evidenceRemoveBtn} onPress={() => onChange(null)}>
            <Text style={s.evidenceRemoveText}>Hapus</Text>
          </TouchableOpacity>
        </View>
      )}
    </View>
  );

  const plotsForField = (fieldId) => fieldPlots.filter(
    (plot) => String(plot.field_id) === String(fieldId)
  );

  const openAddPlotModal = (fieldId, target) => {
    if (!fieldId) {
      Alert.alert('Pilih Lahan', 'Pilih lahan utama sebelum menambahkan petakan.');
      return;
    }
    setNewPlotFieldId(String(fieldId));
    setPlotTarget(target === 'calc' ? 'calc' : 'add');
    setNewPlotName('');
    setShowAddPlot(true);
  };

  const saveNewPlot = async () => {
    const fieldId = Number(newPlotFieldId);
    const name = String(newPlotName || '').trim();
    if (!Number.isInteger(fieldId) || fieldId <= 0) {
      Alert.alert('Validasi', 'Lahan utama tidak valid.');
      return;
    }
    if (!name) {
      Alert.alert('Validasi', 'Nama petakan wajib diisi.');
      return;
    }
    try {
      setSavingPlot(true);
      const response = await axios.post(`${API_BASE}/harvests/field-plots`, {
        field_id: fieldId,
        name,
      }, { headers: authHdr });
      const created = response?.data;
      if (!created?.plot_id) throw new Error('Petakan tersimpan tetapi ID tidak diterima');
      setFieldPlots((prev) => [...prev.filter((item) => String(item.plot_id) !== String(created.plot_id)), created]
        .sort((a, b) => String(a.field_name || '').localeCompare(String(b.field_name || ''), 'id') || String(a.name || '').localeCompare(String(b.name || ''), 'id')));
      if (plotTarget === 'calc') setCalcPlotId(String(created.plot_id));
      else setAddPlotId(String(created.plot_id));
      setShowAddPlot(false);
      setNewPlotName('');
      Alert.alert('Sukses', `Petakan “${created.name}” ditambahkan dan langsung dipilih.`);
    } catch (e) {
      const msg = e?.response?.data?.error || e?.message || 'Gagal menambahkan petakan';
      Alert.alert('Error', msg);
    } finally {
      setSavingPlot(false);
    }
  };

  const PlotPicker = ({ fieldId, value, onChange, target }) => {
    const options = plotsForField(fieldId);
    return (
      <View style={s.plotPickerBox}>
        <View style={s.plotPickerHead}>
          <View style={{ flex: 1 }}>
            <Text style={s.plotPickerTitle}>Petakan / Sub-lahan</Text>
            <Text style={s.plotPickerHint}>Khusus tracking hasil panen pada lahan ini.</Text>
          </View>
          <TouchableOpacity
            style={[s.plotAddBtn, !fieldId && { opacity: 0.5 }]}
            onPress={() => openAddPlotModal(fieldId, target)}
            disabled={!fieldId}
            activeOpacity={0.85}
          >
            <Text style={s.plotAddBtnText}>+ Tambah</Text>
          </TouchableOpacity>
        </View>
        <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={s.plotPillsRow}>
          <TouchableOpacity
            style={[s.pill, !value && s.pillActive]}
            onPress={() => onChange(null)}
            activeOpacity={0.9}
          >
            <Text style={[s.pillText, !value && s.pillTextActive]}>Tanpa petakan / gabungan</Text>
          </TouchableOpacity>
          {options.map((plot) => {
            const active = String(value) === String(plot.plot_id);
            return (
              <TouchableOpacity
                key={plot.plot_id}
                style={[s.pill, active && s.pillActive]}
                onPress={() => onChange(String(plot.plot_id))}
                activeOpacity={0.9}
              >
                <Text style={[s.pillText, active && s.pillTextActive]}>{plot.name}</Text>
              </TouchableOpacity>
            );
          })}
        </ScrollView>
        {!options.length && (
          <Text style={s.plotEmptyText}>Belum ada petakan. Tekan “+ Tambah” untuk membuatnya.</Text>
        )}
      </View>
    );
  };

  // ====== Edit price per harvest ======
  const openEditPrice = (harvest) => {
    setEditPriceHarvest(harvest);
    setEditPriceValue(String(harvest?.price_per_kg ?? '0'));
    setEditPriceModal(true);
  };

  const saveEditPrice = async () => {
    const h = editPriceHarvest;
    if (!h?.id) { setEditPriceModal(false); return; }

    const price = parseInt(editPriceValue || '0', 10);
    if (!(price >= 0)) { Alert.alert('Validasi', 'Harga/kg tidak valid.'); return; }

    try {
      setSavingPrice(true);

      // prefer PATCH, fallback to PUT automatically if API rejects PATCH
      const payload = { price_per_kg: price };
      try {
        await axios.patch(`${API_BASE}/harvests/${h.id}`, payload, { headers: authHdr });
      } catch (e1) {
        const status = e1?.response?.status;
        if (status === 404 || status === 405) {
          await axios.put(`${API_BASE}/harvests/${h.id}`, payload, { headers: authHdr });
        } else {
          throw e1;
        }
      }

      setEditPriceModal(false);
      setEditPriceHarvest(null);
      await fetchAll({ silent: true });
      Alert.alert('Sukses', 'Harga/kg diperbarui.');
    } catch (e) {
      const msg = e?.response?.data?.error || e?.message || 'Gagal update harga/kg';
      if (Platform.OS === 'web') console.error(msg);
      Alert.alert('Error', msg);
    } finally {
      setSavingPrice(false);
    }
  };

  const resetPaymentForm = () => {
    setPaymentAmount('');
    setPaymentNote('');
    setPaymentDate(new Date().toISOString().slice(0,10));
    setPaymentMethod('transfer');
    setEditPaymentId(null);
  };

  const loadPaymentRows = async (harvestId, { quiet = false } = {}) => {
    if (!harvestId) return [];
    try {
      if (!quiet) setPaymentRowsLoading(true);
      const response = await axios.get(`${API_BASE}/harvests/${harvestId}/payments`, { headers: authHdr });
      const rows = Array.isArray(response?.data?.items) ? response.data.items : [];
      setPaymentRows(rows);
      return rows;
    } catch (e) {
      const msg = e?.response?.data?.error || e?.message || 'Gagal memuat riwayat pembayaran';
      if (Platform.OS === 'web') console.error(msg);
      Alert.alert('Error', msg);
      return [];
    } finally {
      if (!quiet) setPaymentRowsLoading(false);
    }
  };

  const openPaymentModal = async (harvest) => {
    setPaymentHarvest(harvest);
    setPaymentRows([]);
    resetPaymentForm();
    setPaymentModalOpen(true);
    await loadPaymentRows(harvest?.id);
  };

  const closePaymentModal = () => {
    if (paymentUpdatingId) return;
    setPaymentModalOpen(false);
    setPaymentHarvest(null);
    setPaymentRows([]);
    resetPaymentForm();
  };

  const editPaymentRow = (row) => {
    setEditPaymentId(row.payment_id);
    setPaymentAmount(String(Number(row.amount || 0)));
    setPaymentDate(String(row.payment_date || '').slice(0, 10));
    setPaymentMethod(String(row.method || 'transfer'));
    setPaymentNote(String(row.note || ''));
  };

  const savePaymentAmount = async () => {
    if (!paymentHarvest?.id) return;
    const amount = parseInputNumber(paymentAmount, { allowDecimal: true, defaultValue: 0 });
    if (!Number.isFinite(amount) || amount <= 0) {
      Alert.alert('Validasi', 'Nominal pembayaran harus lebih dari Rp0.');
      return;
    }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(paymentDate || '')) {
      Alert.alert('Validasi', 'Tanggal pembayaran harus berformat YYYY-MM-DD.');
      return;
    }

    const receivedWithoutEditedRow = paymentRows.reduce((sum, row) => (
      String(row.payment_id) === String(editPaymentId) ? sum : sum + Number(row.amount || 0)
    ), 0);
    if (receivedWithoutEditedRow + amount > paymentModalTotal + 0.001) {
      Alert.alert('Validasi', `Pembayaran melebihi sisa yang dapat dicatat ${formatIDR(Math.max(0, paymentModalTotal - receivedWithoutEditedRow))}.`);
      return;
    }

    try {
      setPaymentUpdatingId(String(paymentHarvest.id));
      const payload = {
        amount,
        payment_date: paymentDate,
        method: paymentMethod,
        note: paymentNote.trim() || null,
      };
      if (editPaymentId) {
        await axios.patch(`${API_BASE}/harvests/${paymentHarvest.id}/payments/${editPaymentId}`, payload, { headers: authHdr });
      } else {
        await axios.post(`${API_BASE}/harvests/${paymentHarvest.id}/payments`, payload, { headers: authHdr });
      }
      await loadPaymentRows(paymentHarvest.id, { quiet: true });
      await fetchAll({ silent: true });
      resetPaymentForm();
      const newlyReceived = receivedWithoutEditedRow + amount;
      const pending = Math.max(0, paymentModalTotal - newlyReceived);
      Alert.alert(
        editPaymentId ? 'DP diperbarui' : 'DP ditambahkan',
        pending <= 0 ? 'Tagihan hasil panen sudah lunas.' : `Sisa pembayaran ${formatIDR(pending)}.`
      );
    } catch (e) {
      const msg = e?.response?.data?.error || e?.message || 'Gagal menyimpan pembayaran';
      if (Platform.OS === 'web') console.error(msg);
      Alert.alert('Error', msg);
    } finally {
      setPaymentUpdatingId(null);
    }
  };

  const voidPaymentRow = (row) => {
    if (!paymentHarvest?.id || !row?.payment_id) return;
    const run = async () => {
      try {
        setPaymentUpdatingId(String(paymentHarvest.id));
        await axios.delete(`${API_BASE}/harvests/${paymentHarvest.id}/payments/${row.payment_id}`, { headers: authHdr });
        await loadPaymentRows(paymentHarvest.id, { quiet: true });
        await fetchAll({ silent: true });
        if (String(editPaymentId) === String(row.payment_id)) resetPaymentForm();
        Alert.alert('Pembayaran dibatalkan', 'Nominal dihapus dari total diterima, tetapi jejak pembatalan tetap tersimpan.');
      } catch (e) {
        const msg = e?.response?.data?.error || e?.message || 'Gagal membatalkan pembayaran';
        if (Platform.OS === 'web') console.error(msg);
        Alert.alert('Error', msg);
      } finally {
        setPaymentUpdatingId(null);
      }
    };
    const message = `Batalkan pembayaran ${formatIDR(row.amount)} tanggal ${fmtDateShort(row.payment_date)}?`;
    if (Platform.OS === 'web') {
      if (window.confirm(message)) run();
      return;
    }
    Alert.alert('Batalkan pembayaran?', message, [
      { text: 'Kembali', style: 'cancel' },
      { text: 'Ya, Batalkan', style: 'destructive', onPress: run },
    ]);
  };

  const confirmMarkPaid = (harvest) => {
    const gross = Number(harvest?.total ?? (Number(harvest?.kg || 0) * Number(harvest?.price_per_kg || 0)));
    const due = Math.max(0, gross - Math.max(0, Number(harvest?.combine_cost || 0)));
    const received = Math.min(due, Math.max(0, Number(harvest?.down_payment_amount || 0)));
    const remaining = Math.max(0, due - received);
    if (remaining <= 0) return;
    const message = `Catat pelunasan ${formatIDR(remaining)} dari ${harvest?.buyer || 'toke/tengkulak'}?`;
    const run = async () => {
      try {
        setPaymentUpdatingId(String(harvest.id));
        await axios.post(
          `${API_BASE}/harvests/${harvest.id}/payments`,
          { amount: remaining, payment_date: new Date().toISOString().slice(0,10), method: 'other', note: 'Pelunasan' },
          { headers: authHdr }
        );
        await fetchAll({ silent: true });
        Alert.alert('Pembayaran lunas', 'Pelunasan ditambahkan ke riwayat pembayaran.');
      } catch (e) {
        const msg = e?.response?.data?.error || e?.message || 'Gagal mencatat pelunasan';
        if (Platform.OS === 'web') console.error(msg);
        Alert.alert('Error', msg);
      } finally {
        setPaymentUpdatingId(null);
      }
    };
    if (Platform.OS === 'web') {
      if (window.confirm(`Tandai lunas?\n\n${message}`)) run();
      return;
    }
    Alert.alert('Tandai lunas?', message, [
      { text: 'Batal', style: 'cancel' },
      { text: 'Ya, Catat Pelunasan', onPress: run },
    ]);
  };

  const paymentModalGross = Number(paymentHarvest?.total ?? (Number(paymentHarvest?.kg || 0) * Number(paymentHarvest?.price_per_kg || 0)));
  const paymentModalCombineCost = Math.max(0, Number(paymentHarvest?.combine_cost || 0));
  const paymentModalTotal = Math.max(0, paymentModalGross - paymentModalCombineCost);
  const paymentModalReceived = paymentRows.reduce((sum, row) => sum + Number(row.amount || 0), 0);
  const editingPaymentAmount = Number(paymentRows.find((row) => String(row.payment_id) === String(editPaymentId))?.amount || 0);
  const paymentDraftAmount = Math.max(0, parseInputNumber(paymentAmount, { allowDecimal: true, defaultValue: 0 }));
  const paymentModalPending = Math.max(0, paymentModalTotal - paymentModalReceived);
  const paymentPendingAfterSave = Math.max(0, paymentModalTotal - paymentModalReceived + editingPaymentAmount - paymentDraftAmount);
  const paymentAvailableToAllocate = Math.max(0, paymentModalPending + editingPaymentAmount);

  const openCombineModal = (harvest) => {
    setCombineHarvest(harvest);
    setCombineCostValue(String(Math.max(0, Number(harvest?.combine_cost || 0))));
    setCombineOperator(String(harvest?.combine_operator || ''));
    setCombineNote(String(harvest?.combine_note || ''));
    setCombineModalOpen(true);
  };

  const saveCombineCost = async () => {
    if (!combineHarvest?.id) return;
    const cost = parseInputNumber(combineCostValue, { allowDecimal: true, defaultValue: 0 });
    if (!Number.isFinite(cost) || cost < 0) {
      Alert.alert('Validasi', 'Biaya Combine Harvester tidak valid.');
      return;
    }
    try {
      setCombineUpdatingId(String(combineHarvest.id));
      await axios.patch(
        `${API_BASE}/harvests/${combineHarvest.id}/combine-cost`,
        {
          combine_cost: cost,
          operator: combineOperator.trim() || null,
          note: combineNote.trim() || null,
        },
        { headers: authHdr }
      );
      setCombineModalOpen(false);
      setCombineHarvest(null);
      await fetchAll({ silent: true });
      Alert.alert(
        cost > 0 ? 'Biaya combine tersimpan' : 'Biaya combine dihapus',
        cost > 0
          ? `Biaya ${formatIDR(cost)} sudah mengurangi hasil bersih dan otomatis masuk menu Pengeluaran.`
          : 'Biaya pada hasil panen dan catatan Pengeluaran terkait sudah dihapus.'
      );
    } catch (e) {
      const msg = e?.response?.data?.error || e?.message || 'Gagal menyimpan biaya combine';
      if (Platform.OS === 'web') console.error(msg);
      Alert.alert('Error', msg);
    } finally {
      setCombineUpdatingId(null);
    }
  };

  const combineModalTotal = Number(combineHarvest?.total ?? (Number(combineHarvest?.kg || 0) * Number(combineHarvest?.price_per_kg || 0)));
  const combineModalKg = Math.max(0, Number(combineHarvest?.kg || 0));
  const combineDraftCost = Math.max(0, parseInputNumber(combineCostValue, { allowDecimal: true, defaultValue: 0 }));
  const combineDraftNet = combineModalTotal - combineDraftCost;

  const HarvestRow = ({ h }) => {
    const total = Number(h.total ?? (Number(h.kg||0) * Number(h.price_per_kg||0)));
    const fieldName = idToName[String(h.field_id)] || 'Lahan';
    const harvestLabel = String(h.notes || '').trim();

    const receiptUrl = normalizeUrl(h.receipt_url);

    const photoUrl =
      normalizeUrl(h.receipt_photo_url) ||
      normalizeUrl(h.receipt_image_url) ||
      (receiptUrl && isLikelyImageUrl(receiptUrl) ? receiptUrl : null);

    const pdfUrl = receiptUrl && !isLikelyImageUrl(receiptUrl) ? receiptUrl : null;

    // ✅ ini yang bikin error kamu hilang
    const evidenceUrl = normalizeUrl(h.evidence_url) || normalizeUrl(h.evidenceUrl) || normalizeUrl(h.evidence_photo_url) || normalizeUrl(h.evidencePhotoUrl);
    const isEvidenceUploading = String(evidenceUploadId || '') === String(h.id);
    const combineCost = Math.max(0, Number(h.combine_cost || 0));
    const netRevenue = total - combineCost;
    const paymentDue = Math.max(0, netRevenue);
    const storedPayment = Math.max(0, Number(h.down_payment_amount || 0));
    const paymentReceived = Math.min(paymentDue, storedPayment);
    const paymentPending = Math.max(0, paymentDue - paymentReceived);
    const paymentState = paymentPending <= 0 ? 'paid' : (paymentReceived > 0 ? 'partial' : 'unpaid');
    const isPaymentUpdating = String(paymentUpdatingId || '') === String(h.id);
    const isCombineUpdating = String(combineUpdatingId || '') === String(h.id);

    const onViewWeigh = async () => {
      setViewWeighLoading(true);
      setViewWeighHarvestId(h.id);
      setViewWeighRows([]);
      try {
        const res = await axios.get(`${API_BASE}/harvests/${h.id}/weighings`, { headers: authHdr });
        setViewWeighRows(Array.isArray(res.data?.weighings) ? res.data.weighings : []);
        setViewWeighModal(true);
      } catch (e) {
        const msg = e?.response?.data?.error || e?.message || 'Gagal memuat timbangan';
        Alert.alert('Error', msg);
      } finally {
        setViewWeighLoading(false);
      }
    };

    return (
      <View style={s.row}>
        <View style={s.rowHeader}>
          <View style={{ flex: 1, paddingRight: 10 }}>
            <Text style={s.rowTitle} numberOfLines={1}>
              {fieldName}
            </Text>
            {!!h.plot_name && (
              <View style={s.plotTag}>
                <Text style={s.plotTagText}>Petakan · {h.plot_name}</Text>
              </View>
            )}
            {!!harvestLabel && (
              <Text style={s.harvestLabel} numberOfLines={1}>{harvestLabel}</Text>
            )}
            <Text style={s.rowSub} numberOfLines={1}>
              {fmtDateShort(h.date)} • {Number(h.kg||0).toLocaleString('id-ID')} kg
            </Text>
          </View>

          <View style={s.rowActions}>
            {/* Receipt PDF (kalau receipt_url bentuk pdf) */}
            {pdfUrl && (
              <TouchableOpacity onPress={() => openBon(pdfUrl)} style={s.badgeLink} activeOpacity={0.9}>
                <Text style={s.badgeLinkText}>Bon PDF</Text>
              </TouchableOpacity>
            )}

            {/* Bon fisik (foto receipt) */}
            <TouchableOpacity
              onPress={() => {
                if (isEvidenceUploading) return;
                if (evidenceUrl) openReceiptViewer(evidenceUrl);
                else uploadEvidenceForHarvest(h.id);
              }}
              style={[s.badgeLinkLight, isEvidenceUploading && { opacity: 0.65 }]}
              disabled={isEvidenceUploading}
              activeOpacity={0.9}
            >
              <Text style={s.badgeLinkLightText}>
                {isEvidenceUploading ? 'Mengunggah…' : (evidenceUrl ? 'Lihat Bon Fisik' : 'Upload Bon Fisik')}
              </Text>
            </TouchableOpacity>

          </View>
        </View>

        <View style={s.rowCols}>
          <View style={s.rowCol}>
            <Text style={s.rowKey}>Hasil</Text>
            <Text style={s.rowVal}>
              {Number(h.kg||0).toLocaleString('id-ID')} kg
              <Text style={s.rowDim}>  · {toTon(h.kg).toFixed(2)} ton</Text>
            </Text>
          </View>

          <View style={s.rowCol}>
            <View style={{ flexDirection:'row', alignItems:'center', justifyContent:'space-between' }}>
              <Text style={s.rowKey}>Harga/kg</Text>
              <TouchableOpacity onPress={() => openEditPrice(h)} style={s.editMiniBtn} activeOpacity={0.9}>
                <Text style={s.editMiniBtnText}>Edit</Text>
              </TouchableOpacity>
            </View>
            <Text style={s.rowVal}>{formatIDR(h.price_per_kg)}</Text>
          </View>

          <View style={s.rowCol}>
            <Text style={s.rowKey}>Total</Text>
            <Text style={[s.rowVal, { color:T.textGreen }]}>{formatIDR(total)}</Text>
          </View>

          <View style={s.rowCol}>
            <View style={{ flexDirection:'row', alignItems:'center', justifyContent:'space-between' }}>
              <Text style={s.rowKey}>Odong-odong / Combine</Text>
              <TouchableOpacity onPress={() => openCombineModal(h)} style={s.editMiniBtn} disabled={isCombineUpdating} activeOpacity={0.9}>
                <Text style={s.editMiniBtnText}>{combineCost > 0 ? 'Edit' : 'Tambah'}</Text>
              </TouchableOpacity>
            </View>
            <Text style={[s.rowVal, { color: '#9A5A00' }]}>{formatIDR(combineCost)}</Text>
            <Text style={s.combineLinkedText}>
              {h.combine_operator || 'Otomatis tercatat sebagai pengeluaran'}
            </Text>
          </View>

          <View style={s.rowCol}>
            <Text style={s.rowKey}>Hasil Bersih</Text>
            <Text style={[s.rowVal, { color: T.textGreen }, netRevenue < 0 && s.combineNetNegative]}>
              {formatIDR(netRevenue)}
            </Text>
            <Text style={s.rowDim}>Kotor − biaya combine</Text>
          </View>
        </View>

        <View style={[
          s.paymentBox,
          paymentState === 'paid' ? s.paymentBoxPaid : (paymentState === 'partial' ? s.paymentBoxPartial : s.paymentBoxUnpaid),
        ]}>
          <View style={s.paymentHead}>
            <View style={[
              s.paymentDot,
              paymentState === 'paid' ? s.paymentDotPaid : (paymentState === 'partial' ? s.paymentDotPartial : s.paymentDotUnpaid),
            ]} />
            <Text style={[
              s.paymentStatus,
              paymentState === 'paid' ? s.paymentStatusPaid : (paymentState === 'partial' ? s.paymentStatusPartial : s.paymentStatusUnpaid),
            ]}>
              {paymentState === 'paid' ? 'Sudah Dibayar' : (paymentState === 'partial' ? 'DP / Dibayar Sebagian' : 'Belum Dibayar')}
            </Text>
          </View>
          <Text style={s.paymentBuyer}>
            {h.buyer ? `Toke/tengkulak: ${h.buyer}` : 'Toke/tengkulak belum dicatat'}
            {paymentState === 'paid' && h.paid_at ? ` · Lunas ${fmtDateShort(h.paid_at)}` : ''}
          </Text>
          <View style={s.paymentNumbers}>
            <View style={s.paymentNumberItem}>
              <Text style={s.paymentNumberKey}>Sudah diterima</Text>
              <Text style={s.paymentNumberValue}>{formatIDR(paymentReceived)}</Text>
            </View>
            <View style={s.paymentNumberItem}>
              <Text style={s.paymentNumberKey}>Sisa pembayaran</Text>
              <Text style={[s.paymentNumberValue, paymentPending > 0 && s.paymentPendingValue]}>{formatIDR(paymentPending)}</Text>
            </View>
            <View style={s.paymentNumberItem}>
              <Text style={s.paymentNumberKey}>Tagihan setelah combine</Text>
              <Text style={s.paymentNumberValue}>{formatIDR(paymentDue)}</Text>
            </View>
          </View>
          <View style={s.paymentActions}>
            <TouchableOpacity
              style={s.paymentActionBtn}
              onPress={() => openPaymentModal(h)}
              disabled={isPaymentUpdating}
              activeOpacity={0.9}
            >
              <Text style={s.paymentActionText}>Riwayat / Tambah DP</Text>
            </TouchableOpacity>
            {paymentPending > 0 && (
              <TouchableOpacity
                style={[s.paymentActionBtn, s.paymentActionPrimary, isPaymentUpdating && { opacity: 0.65 }]}
                onPress={() => confirmMarkPaid(h)}
                disabled={isPaymentUpdating}
                activeOpacity={0.9}
              >
                <Text style={[s.paymentActionText, s.paymentActionPrimaryText]}>
                  {isPaymentUpdating ? 'Menyimpan…' : 'Tandai Lunas'}
                </Text>
              </TouchableOpacity>
            )}
          </View>
        </View>

        {(!!pdfUrl || !!evidenceUrl) && (
          <View style={s.photoHintRow}>
            <View style={s.dotOk} />
            <Text style={s.photoHintText}>
              {pdfUrl && evidenceUrl ? 'Bon & evidence tersedia' : (pdfUrl ? 'Bon tersedia' : 'Evidence tersedia')}
            </Text>
          </View>
        )}
      </View>
    );
  };

  // ====== Kalkulator Timbangan (Net KG per timbang) ======
  const onOpenCalc = () => {
    setShowCalc(true);
    setShowCalcDatePicker(false);
    if (!calcFieldId && fields[0]) setCalcFieldId(String(fields[0].id));
  };
  const calcDateObj = useMemo(() => {
    if (calcDate && /^\d{4}-\d{2}-\d{2}$/.test(calcDate)) {
      const [yy, mm, dd] = calcDate.split('-').map(Number);
      const d = new Date(yy, mm - 1, dd);
      if (!Number.isNaN(d.getTime())) return d;
    }
    return new Date();
  }, [calcDate]);
  const onNativeCalcDateChange = (event, selectedDate) => {
    if (Platform.OS !== 'ios') setShowCalcDatePicker(false);
    if (selectedDate) setCalcDate(toYMD(selectedDate));
  };
  const tryBuildPendingWeighRow = ({ silentIfEmpty = false } = {}) => {
    const kgRaw = String(calcKg ?? '').trim();
    const k = parseInputNumber(calcKg, { allowDecimal: true, defaultValue: 0 });
    const sacksParsed = parseInputNumber(calcSacks, { allowDecimal: false, defaultValue: 1 });
    const s = Math.max(1, sacksParsed || 1);

    if (!(k > 0)) {
      if (!silentIfEmpty || kgRaw) Alert.alert('Validasi', 'Netto (kg) harus > 0.');
      return null;
    }
    return { id: Date.now() + Math.random(), kg: k, sacks: s };
  };

  const addWeighRow = () => {
    const row = tryBuildPendingWeighRow();
    if (!row) return;
    setCalcRows((prev) => [row, ...prev]);
    setCalcKg('');
    setCalcSacks(DEFAULT_SACKS);
    // Keep the loop going: farmer weighs many sacks in a row, so jump the
    // keyboard focus straight back to Netto instead of making them tap in.
    setTimeout(() => calcKgInputRef.current?.focus(), 0);
  };
  const removeWeighRow = (id) => setCalcRows((prev) => prev.filter(x => x.id !== id));
  const resetCalc = () => { setCalcRows([]); setCalcKg(''); setCalcSacks(DEFAULT_SACKS); setCalcPrice(''); setNotesCalc(''); setBuyerCalc(''); setCalcEvidenceAsset(null); setCalcPlotId(null); };
  const confirmResetCalc = () => {
    if (!calcRows.length && !calcKg && !calcPrice && !notesCalc && !buyerCalc) return;
    Alert.alert(
      'Reset semua input?',
      'Semua baris timbangan dan data yang sudah diisi akan dihapus.',
      [
        { text: 'Batal', style: 'cancel' },
        { text: 'Ya, Reset', style: 'destructive', onPress: resetCalc },
      ]
    );
  };

  const calcTotalNet   = useMemo(() => calcRows.reduce((a,b)=>a + Number(b.kg||0),0), [calcRows]);
  const calcTotalSacks = useMemo(() => calcRows.reduce((a,b)=>a + Number(b.sacks||0),0), [calcRows]);
  const pendingNetKg = useMemo(
    () => parseInputNumber(calcKg, { allowDecimal: true, defaultValue: 0 }),
    [calcKg]
  );
  const hasPendingWeighing = pendingNetKg > 0;
  const calcTotalNetProjected = calcTotalNet + (hasPendingWeighing ? pendingNetKg : 0);
  const pendingSacks = useMemo(
    () => (hasPendingWeighing ? Math.max(1, parseInputNumber(calcSacks, { allowDecimal: false, defaultValue: 1 })) : 0),
    [hasPendingWeighing, calcSacks]
  );
  const calcTotalSacksProjected = calcTotalSacks + pendingSacks;
  const calcAvgNetPerSackProjected = useMemo(
    () => (calcTotalSacksProjected > 0 ? (calcTotalNetProjected / calcTotalSacksProjected) : 0),
    [calcTotalNetProjected, calcTotalSacksProjected]
  );
  const calcPriceNum = useMemo(
    () => parseInputNumber(calcPrice, { allowDecimal: false, defaultValue: 0 }),
    [calcPrice]
  );
  const calcRevenue = useMemo(() => (calcTotalNetProjected * calcPriceNum), [calcTotalNetProjected, calcPriceNum]);
  const canSaveCalc = !!calcFieldId && (calcRows.length > 0 || hasPendingWeighing);

  // finish & save weighing -> use POST /harvests/bon
  const finishAndSaveWeighing = async () => {
    if (!calcFieldId) { Alert.alert('Validasi','Pilih lahan.'); return; }
    const fieldNum = Number(calcFieldId);
    if (!Number.isFinite(fieldNum) || fieldNum <= 0) { Alert.alert('Validasi','Lahan tidak valid.'); return; }
    let rowsForSave = calcRows.slice();
    const pendingRow = tryBuildPendingWeighRow({ silentIfEmpty: true });
    if (pendingRow) {
      rowsForSave = [pendingRow, ...rowsForSave];
      setCalcRows(rowsForSave);
      setCalcKg('');
      setCalcSacks(DEFAULT_SACKS);
    }

    if (!rowsForSave.length) { Alert.alert('Validasi','Tambah minimal 1 baris timbangan.'); return; }

    const totalKg = Number(rowsForSave.reduce((a, b) => a + Number(b.kg || 0), 0));
    if (!(totalKg > 0)) { Alert.alert('Validasi','Total netto harus > 0.'); return; }
    const price = parseInputNumber(calcPrice, { allowDecimal: false, defaultValue: 0 });
    if (!(price >= 0)) { Alert.alert('Validasi','Harga/kg tidak valid.'); return; }

    try {
      setSavingCalc(true);
      const hadEvidencePhoto = !!calcEvidenceAsset?.uri;

      const payloadRows = rowsForSave.slice().reverse().map((r) => ({
        gross_kg: Number(r.kg || 0),
        net_kg: Number(r.kg || 0),
        sacks: Math.max(1, Number(r.sacks || 1)),
        tare_per_sack: 0,
        note: ''
      }));

      const payload = {
        field_id: fieldNum,
        plot_id: calcPlotId ? Number(calcPlotId) : null,
        date: calcDate || toYMD(new Date()),
        price_per_kg: price,
        buyer: buyerCalc || null,
        notes: notesCalc || null,
        rows: payloadRows
      };

      const res = await axios.post(`${API_BASE}/harvests/bon`, payload, { headers: authHdr });
      let photoError = null;
      let uploadedEvidenceUrl = null;
      const harvestId = res?.data?.harvest_id;
      if (calcEvidenceAsset?.uri && harvestId) {
        try {
          const uploadResponse = await uploadEvidenceAsset(harvestId, calcEvidenceAsset);
          uploadedEvidenceUrl = normalizeUrl(uploadResponse?.data?.evidence_url);
        } catch (uploadError) {
          photoError = uploadError?.response?.data?.error || uploadError?.message || 'Upload foto bon gagal';
        }
      }

      const data = res.data || {};
      const absUrl = data.url || (data.receipt_url ? `${API_BASE}${data.receipt_url}` : null);

      setShowCalc(false);
      resetCalc();
      await fetchAll({ silent: true });
      if (hadEvidencePhoto && !photoError && uploadedEvidenceUrl) {
        setEvidenceNotice({
          title: 'Bon fisik berhasil disimpan',
          detail: 'Lampiran sudah terhubung ke hasil panen dan siap dilihat.',
          url: uploadedEvidenceUrl,
        });
      }

      if (absUrl) {
        if (Platform.OS === 'web') window.open(absUrl, '_blank');
        else Linking.openURL(absUrl).catch(() => Alert.alert('Sukses', 'Bon dibuat. Tapi gagal membuka PDF otomatis.'));
      }

      Alert.alert(
        photoError ? 'Panen Tersimpan' : 'Sukses',
        photoError
          ? `Bon dan hasil panen tersimpan, tetapi foto gagal diunggah: ${photoError}. Foto masih dapat ditambahkan dari card panen.`
          : hadEvidencePhoto
            ? 'Bon, hasil panen, dan foto bon fisik berhasil disimpan.'
            : 'Bon dan hasil panen berhasil disimpan.'
      );
    } catch (e) {
      const msg = e?.response?.data?.error || e?.message || 'Gagal menyimpan hasil timbangan';
      if (Platform.OS === 'web') console.error(msg);
      Alert.alert('Error', msg);
    } finally {
      setSavingCalc(false);
    }
  };

  // fetch saved weighings for view modal
  const fetchWeighingsFor = async (harvestId) => {
    setViewWeighLoading(true);
    setViewWeighRows([]);
    try {
      const res = await axios.get(`${API_BASE}/harvests/${harvestId}/weighings`, { headers: authHdr });
      setViewWeighRows(Array.isArray(res.data?.weighings) ? res.data.weighings : []);
    } catch (e) {
      const msg = e?.response?.data?.error || e?.message || 'Gagal memuat timbangan';
      Alert.alert('Error', msg);
    } finally {
      setViewWeighLoading(false);
    }
  };

  // ===== Compact filter labels =====
  const selectedSeasonLabel = selectedSeason
    ? getSeasonName(selectedSeason)
    : (seasonLoading ? 'Memuat musim...' : 'Belum ada musim');
  const selectedSeasonRange = selectedSeason
    ? getSeasonRangeLabel(selectedSeason)
    : 'Tambahkan musim agar data panen bisa difilter.';
  const seasonStatusLabel = selectedSeason
    ? (selectedSeason.end_date == null ? 'Aktif' : 'Arsip')
    : 'Belum dipilih';
  const sortLabel   = `${sortKey === 'date' ? 'Tanggal' : (sortKey === 'yield' ? 'Hasil' : 'Pendapatan')} ${sortDir === 'asc' ? '↑' : '↓'}`;
  const openAddModal = () => {
    setShowAdd(true);
    if (!addFieldId && fields[0]) setAddFieldId(String(fields[0].id));
  };

  return (
    <View style={s.screen}>
      <View style={s.bgTop} />
      <View style={s.bgBottom} />

      <ScrollView
        contentInsetAdjustmentBehavior="automatic"
        showsVerticalScrollIndicator={false}
        contentContainerStyle={s.wrap}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor={T.brandGreen} />}
      >
        <View style={[s.container, contentWidthStyle]}>
          <EnterpriseHeader
            title="Rekap Hasil Panen"
            subtitle="Edit harga/kg per panen dan kelola bukti bon secara terstruktur"
            right={(
              <View style={s.heroIconWrap}>
                <PadiProIcon name="harvest" size={20} color="#f4fff8" strokeWidth={2} />
              </View>
            )}
          />

          <View style={s.heroActions}>
            <TouchableOpacity
              onPress={onOpenCalc}
              style={[s.heroActionBtn, s.heroActionPrimary]}
              activeOpacity={0.9}
            >
              <PadiProIcon name="operations" size={14} color="#fff" strokeWidth={2} />
              <Text style={s.heroActionTextPrimary}>Timbang</Text>
            </TouchableOpacity>
            <TouchableOpacity
              onPress={onExportPDF}
              style={[s.heroActionBtn, s.heroActionGhost]}
              activeOpacity={0.9}
            >
              <PadiProIcon name="report" size={14} color={T.brandBlue} strokeWidth={2} />
              <Text style={s.heroActionTextGhost}>Export PDF</Text>
            </TouchableOpacity>
            <TouchableOpacity
              onPress={openAddModal}
              style={[s.heroActionBtn, s.heroActionGhost]}
              activeOpacity={0.9}
            >
              <PadiProIcon name="inventory" size={14} color={T.brandBlue} strokeWidth={2} />
              <Text style={s.heroActionTextGhost}>Tambah Panen</Text>
            </TouchableOpacity>
          </View>

          {!!evidenceNotice && (
            <View style={s.evidenceSuccess}>
              <View style={{ flex: 1 }}>
                <Text style={s.evidenceSuccessTitle}>{evidenceNotice.title}</Text>
                <Text style={s.evidenceSuccessText}>{evidenceNotice.detail}</Text>
              </View>
              {!!evidenceNotice.url && (
                <TouchableOpacity style={s.evidenceSuccessLink} onPress={() => openReceiptViewer(evidenceNotice.url)}>
                  <Text style={s.evidenceSuccessLinkText}>Lihat Bon</Text>
                </TouchableOpacity>
              )}
              <TouchableOpacity style={s.evidenceSuccessClose} onPress={() => setEvidenceNotice(null)}>
                <Text style={s.evidenceSuccessCloseText}>×</Text>
              </TouchableOpacity>
            </View>
          )}

          {/* COMPACT FILTER HEADER */}
          <View style={s.card}>
            <View style={s.compactFilterTop}>
              <View style={{ flex: 1 }}>
                <Text style={s.sectionTitle}>Filter Musim</Text>
                <Text style={s.sectionHint}>
                  {selectedSeasonLabel} • {seasonStatusLabel} • {sortLabel}
                </Text>
                <Text style={s.sectionHintSoft}>{selectedSeasonRange}</Text>
              </View>
              <TouchableOpacity
                onPress={() => setShowFilters(v => !v)}
                style={[s.pill, showFilters && s.pillActive]}
                activeOpacity={0.9}
              >
                <Text style={[s.pillText, showFilters && s.pillTextActive]}>
                  {showFilters ? 'Tutup' : 'Atur'}
                </Text>
              </TouchableOpacity>
            </View>

            {showFilters && (
              <View style={{ marginTop: 10 }}>
                <Text style={s.groupTitle}>Pilih Musim</Text>
                {seasonLoading ? (
                  <View style={s.inlineLoadingRow}>
                    <ActivityIndicator size="small" color={T.brandGreen} />
                    <Text style={s.inlineLoadingText}>Memuat daftar musim...</Text>
                  </View>
                ) : seasons.length ? (
                  <View style={s.seasonGrid}>
                    {seasons.map((season) => {
                      const sid = String(season.season_id);
                      const isActive = season.end_date == null;
                      const isSelected = sid === String(selectedSeasonId);
                      return (
                        <TouchableOpacity
                          key={sid}
                          style={[s.seasonCard, isSelected && s.seasonCardActive]}
                          onPress={() => selectSeason(sid)}
                          activeOpacity={0.92}
                        >
                          <View style={s.seasonCardTop}>
                            <Text style={[s.seasonCardTitle, isSelected && s.seasonCardTitleActive]} numberOfLines={1}>
                              {getSeasonName(season)}
                            </Text>
                            {isActive && (
                              <View style={[s.seasonBadge, isSelected && s.seasonBadgeActive]}>
                                <Text style={[s.seasonBadgeText, isSelected && s.seasonBadgeTextActive]}>Aktif</Text>
                              </View>
                            )}
                          </View>
                          <Text style={[s.seasonCardSub, isSelected && s.seasonCardSubActive]} numberOfLines={1}>
                            {getSeasonRangeLabel(season)}
                          </Text>
                        </TouchableOpacity>
                      );
                    })}
                  </View>
                ) : (
                  <View style={s.emptySeasonBox}>
                    <Text style={s.emptySeasonText}>Belum ada musim tersedia.</Text>
                  </View>
                )}

                {!!activeSeason && String(activeSeason.season_id) !== String(selectedSeasonId) && (
                  <TouchableOpacity
                    style={s.backToActiveBtn}
                    onPress={() => selectSeason(String(activeSeason.season_id))}
                    activeOpacity={0.9}
                  >
                    <Text style={s.backToActiveText}>Kembali ke musim aktif: {getSeasonName(activeSeason)}</Text>
                  </TouchableOpacity>
                )}

                <Text style={[s.groupTitle, { marginTop: 12 }]}>Urutkan</Text>
                <View style={s.pillsRow}>
                  {[ {key:'date',label:'Tanggal'},{key:'yield',label:'Hasil'},{key:'revenue',label:'Pendapatan'} ].map(o => (
                    <TouchableOpacity key={o.key} style={[s.pill, sortKey===o.key && s.pillActive]} onPress={()=>setSortKey(o.key)} activeOpacity={0.9}>
                      <Text style={[s.pillText, sortKey===o.key && s.pillTextActive]}>{o.label}</Text>
                    </TouchableOpacity>
                  ))}
                  <TouchableOpacity style={s.pill} onPress={()=> setSortDir(d => d==='asc' ? 'desc' : 'asc')} activeOpacity={0.9}>
                    <Text style={s.pillText}>{sortDir==='asc'?'Asc ↑':'Desc ↓'}</Text>
                  </TouchableOpacity>
                </View>
              </View>
            )}
          </View>

          {/* SUMMARY */}
          <View style={[s.card, { marginTop: 12 }]}>
            <Text style={s.sectionTitle}>Ringkasan</Text>
            <View style={s.summaryStrip}>
              <Stat label="Total (kg)" value={(summary?.total_kg ?? 0).toLocaleString('id-ID')} sub={`≈ ${toTon(summary?.total_kg||0).toFixed(2)} ton`} />
              <Stat label="Pendapatan Kotor" value={formatIDR(summary?.total_revenue)} sub={`Avg/kg ${formatIDR(Math.round(summary?.avg_price||0))}`} />
              <Stat label="Biaya Combine" value={formatIDR(combineSummary.cost)} sub={`${combineSummary.count} panen memakai combine`} />
              <Stat label="Hasil Bersih" value={formatIDR(combineSummary.net)} sub="Kotor − biaya combine" />
              <Stat label="Lahan Panen" value={summary?.total_fields_harvested ?? 0} />
            </View>
            <View style={s.paymentSummaryRow}>
              <View style={[s.paymentSummaryItem, s.paymentSummaryReceived]}>
                <Text style={s.paymentSummaryLabel}>Pembayaran Diterima</Text>
                <Text style={s.paymentSummaryValue}>{formatIDR(paymentSummary.received)}</Text>
                <Text style={s.paymentSummarySub}>
                  {paymentSummary.paidCount} lunas · {paymentSummary.partialCount} masih DP
                </Text>
              </View>
              <View style={[s.paymentSummaryItem, s.paymentSummaryPending]}>
                <Text style={s.paymentSummaryLabel}>Masih Pending</Text>
                <Text style={[s.paymentSummaryValue, { color: '#9A5A00' }]}>{formatIDR(paymentSummary.pending)}</Text>
                <Text style={s.paymentSummarySub}>
                  {paymentSummary.pendingCount} transaksi belum lunas
                </Text>
              </View>
            </View>
          </View>


            <View style={[s.card, { marginTop: 12 }]}>
              <Text style={s.sectionTitle}>Hasil per Petakan</Text>
              <Text style={s.sectionHint}>
                Total tetap dihitung per hamparan; bagian ini hanya memecah detail hasil tiap petak.
              </Text>

              {plotBreakdown.length ? plotBreakdown.map((group) => (
                <View key={group.field_id} style={s.plotSummaryGroup}>
                  <View style={s.plotSummaryHead}>
                    <View style={{ flex: 1 }}>
                      <Text style={s.plotSummaryField}>{group.field_name}</Text>
                      <Text style={s.plotSummaryMeta}>{group.entries.length} rincian petak</Text>
                    </View>
                    <View style={s.plotSummaryTotalBox}>
                      <Text style={s.plotSummaryTotalKey}>Total Hamparan</Text>
                      <Text style={s.plotSummaryTotalVal}>{group.total_kg.toLocaleString('id-ID')} kg</Text>
                    </View>
                  </View>

                  {group.entries.map((entry) => (
                    <View key={entry.key} style={s.plotSummaryRow}>
                      <View style={s.plotRankBadge}>
                        <Text style={s.plotRankText}>{entry.rank}</Text>
                      </View>
                      <View style={{ flex: 1, minWidth: 150 }}>
                        <View style={s.plotSummaryRowTop}>
                          <Text style={s.plotSummaryName} numberOfLines={1}>{entry.name}</Text>
                          <Text style={s.plotSummaryKg}>{entry.kg.toLocaleString('id-ID')} kg</Text>
                        </View>
                        <View style={s.plotProgressTrack}>
                          <View style={[s.plotProgressFill, { width: `${Math.max(2, entry.share * 100)}%` }]} />
                        </View>
                        <Text style={s.plotSummarySub}>
                          {(entry.share * 100).toFixed(1)}% dari hamparan · {entry.harvest_count} catatan · {formatIDR(entry.revenue)}
                        </Text>
                      </View>
                    </View>
                  ))}
                </View>
              )) : (
                <View style={s.plotSummaryEmpty}>
                  <Text style={s.plotSummaryEmptyText}>
                    {fieldPlots.length
                      ? 'Petakan sudah tersedia. Rincian akan muncul setelah panen disimpan dengan nama petakan.'
                      : 'Belum ada petakan. Buka Tambah Panen atau Timbang, pilih lahan, lalu tekan “+ Tambah”.'}
                  </Text>
                  <View style={s.plotEmptyActions}>
                    <TouchableOpacity style={s.plotEmptyBtn} onPress={openAddModal} activeOpacity={0.9}>
                      <Text style={s.plotEmptyBtnText}>Buka Tambah Panen</Text>
                    </TouchableOpacity>
                    <TouchableOpacity style={s.plotEmptyBtnGhost} onPress={onOpenCalc} activeOpacity={0.9}>
                      <Text style={s.plotEmptyBtnGhostText}>Buka Timbang</Text>
                    </TouchableOpacity>
                  </View>
                </View>
              )}
            </View>

          {/* SEARCH */}
          <View style={[s.card, { marginTop: 12 }]}>
            <Text style={s.sectionTitle}>Cari</Text>
            <TextInput
              value={query}
              onChangeText={setQuery}
              placeholder="Cari lahan / angka…"
              placeholderTextColor={T.textMuted}
              style={s.search}
            />
          </View>

          {/* LIST */}
          <View style={{ marginTop: 12 }}>
            {loading ? (
              <View style={{ marginTop: 8 }}>
                <View style={s.skelRow} />
                <View style={s.skelRow} />
                <View style={s.skelRow} />
              </View>
            ) : items.length ? (
              items.map((h) => <HarvestRow key={h.id} h={h} />)
            ) : (
              <View style={s.emptyBox}>
                <View style={s.emptyIcon}>
                  <PadiProIcon name="harvest" size={18} color={T.brandBlue} strokeWidth={2} />
                </View>
                <Text style={s.emptyTitle}>Belum ada data</Text>
                <Text style={s.emptyText}>
                  {selectedSeason
                    ? `Belum ada panen pada ${getSeasonName(selectedSeason)}.`
                    : 'Coba ubah filter, atau tambahkan panen baru.'}
                </Text>
              </View>
            )}
          </View>

          {/* ADD MODAL (manual) */}
          <Modal visible={showAdd} transparent animationType="fade" onRequestClose={()=>setShowAdd(false)}>
            <View style={s.modalWrap}>
              <View style={[s.modalCard, { maxHeight: '90%' }, isWide && { maxWidth: 760 }]}>
                <Text style={s.modalTitle}>Tambah Hasil Panen</Text>
                <ScrollView
                  showsVerticalScrollIndicator={false}
                  keyboardShouldPersistTaps="handled"
                  contentContainerStyle={{ paddingBottom: 8 }}
                >

                <Text style={s.label}>Pilih Lahan</Text>
                <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{ gap: 8, paddingRight: 8 }}>
                  {fields.map((f) => (
                    <TouchableOpacity
                      key={f.id}
                      style={[s.pill, String(addFieldId) === String(f.id) && s.pillActive]}
                      onPress={() => {
                        setAddFieldId(String(f.id));
                        setAddPlotId(null);
                      }}
                      activeOpacity={0.9}
                    >
                      <Text style={[s.pillText, String(addFieldId) === String(f.id) && s.pillTextActive]}>{f.name}</Text>
                    </TouchableOpacity>
                  ))}
                </ScrollView>

                <PlotPicker
                  fieldId={addFieldId}
                  value={addPlotId}
                  onChange={setAddPlotId}
                  target="add"
                />

                <Text style={s.label}>Tanggal Panen (YYYY-MM-DD)</Text>
                <TextInput value={addDate} onChangeText={setAddDate} placeholder="YYYY-MM-DD" placeholderTextColor={T.textMuted} style={s.input} />

                <View style={s.formGrid}>
                  <View style={{ flex: 1, minWidth: 140 }}>
                    <Text style={s.label}>Hasil (kg)</Text>
                    <TextInput value={addKg} onChangeText={setAddKg} placeholder="0" placeholderTextColor={T.textMuted} keyboardType="numeric" style={s.input} />
                  </View>
                  <View style={{ flex: 1, minWidth: 140 }}>
                    <Text style={s.label}>Harga per kg (IDR)</Text>
                    <TextInput value={addPrice} onChangeText={setAddPrice} placeholder="0" placeholderTextColor={T.textMuted} keyboardType="numeric" style={s.input} />
                  </View>
                </View>

                <Text style={s.label}>Tengkulak / Pembeli (opsional)</Text>
                <TextInput
                  value={addBuyer}
                  onChangeText={setAddBuyer}
                  placeholder="Nama pemberi bon fisik"
                  placeholderTextColor={T.textMuted}
                  style={s.input}
                />

                <EvidencePicker asset={addEvidenceAsset} onChange={setAddEvidenceAsset} />
                </ScrollView>

                <View style={s.modalRow}>
                  <TouchableOpacity style={[s.modalBtn, s.btnGhost]} onPress={()=>setShowAdd(false)} activeOpacity={0.9}>
                    <Text style={[s.modalBtnText, { color: T.text } ]}>Batal</Text>
                  </TouchableOpacity>
                  <TouchableOpacity style={[s.modalBtn, s.btnPrimary, savingAdd && { opacity: 0.7 }]} onPress={onSaveAdd} disabled={savingAdd} activeOpacity={0.9}>
                    <Text style={s.modalBtnText}>{savingAdd ? 'Menyimpan…' : 'Simpan'}</Text>
                  </TouchableOpacity>
                </View>
              </View>
            </View>
          </Modal>

          {/* CALC MODAL */}
          <Modal visible={showCalc} transparent animationType="fade" onRequestClose={()=>setShowCalc(false)}>
            <KeyboardAvoidingView
              style={{ flex: 1 }}
              behavior={Platform.OS === 'ios' ? 'padding' : undefined}
            >
            <View style={s.modalWrap}>
              <View style={[s.modalCard, { maxWidth: 920, width: '95%', maxHeight: '88%' }]}>
                <Text style={s.modalTitle}>Kalkulator Timbangan (Netto/kg)</Text>
                <Text style={s.sectionHint}>
                  Input netto per timbang, lalu simpan sekali untuk otomatis membuat bon PDF dan rekap panen.
                </Text>

                <View style={{ flex: 1 }}>
                  <ScrollView contentContainerStyle={s.calcBody}>
                    <View style={s.calcIntroCard}>
                      <Text style={s.calcIntroTitle}>Status Input</Text>
                      <Text style={s.calcIntroText}>
                        {canSaveCalc
                          ? 'Data sudah siap. Input netto terakhir akan otomatis dimasukkan saat disimpan.'
                          : 'Pilih lahan dan isi minimal satu netto timbangan untuk mengaktifkan simpan.'}
                      </Text>
                      <View style={s.calcMetaRow}>
                        <View style={s.calcMetaChip}>
                          <Text style={s.calcMetaKey}>Baris</Text>
                          <Text style={s.calcMetaVal}>{calcRows.length + (hasPendingWeighing ? 1 : 0)}</Text>
                        </View>
                        <View style={s.calcMetaChip}>
                          <Text style={s.calcMetaKey}>Total Netto</Text>
                          <Text style={s.calcMetaVal}>{calcTotalNetProjected.toLocaleString('id-ID')} kg</Text>
                        </View>
                        <View style={s.calcMetaChip}>
                          <Text style={s.calcMetaKey}>Estimasi</Text>
                          <Text style={s.calcMetaVal}>{formatIDR(calcRevenue)}</Text>
                        </View>
                      </View>
                    </View>

                    <View style={s.calcSection}>
                      <Text style={s.calcSectionTitle}>1. Input Timbangan</Text>
                      <Text style={s.calcSectionHint}>Isi netto & goni — baris langsung muncul di samping untuk dicek.</Text>

                      <View style={s.calcInputListRow}>
                        {/* LEFT: input pad */}
                        <View style={s.calcInputCol}>
                          <Text style={s.rowKey}>Netto (kg)</Text>
                          <TextInput
                            ref={calcKgInputRef}
                            value={calcKg}
                            onChangeText={setCalcKg}
                            placeholder="0"
                            keyboardType="decimal-pad"
                            returnKeyType="next"
                            blurOnSubmit={false}
                            onSubmitEditing={() => calcSacksInputRef.current?.focus()}
                            placeholderTextColor={T.textMuted}
                            style={[s.input, s.inputLg]}
                          />
                          <Text style={[s.rowKey, { marginTop: 8 }]}>Goni</Text>
                          <TextInput
                            ref={calcSacksInputRef}
                            value={calcSacks}
                            onChangeText={setCalcSacks}
                            placeholder="1"
                            keyboardType="number-pad"
                            returnKeyType="done"
                            onSubmitEditing={addWeighRow}
                            placeholderTextColor={T.textMuted}
                            style={[s.input, s.inputLg]}
                          />

                          <View style={s.calcPresetRow}>
                            {[1, 2, 3, 4, 5].map((v) => (
                              <TouchableOpacity
                                key={v}
                                style={[s.calcPresetBtn, String(calcSacks || DEFAULT_SACKS) === String(v) && s.calcPresetBtnActive]}
                                onPress={() => setCalcSacks(String(v))}
                                activeOpacity={0.9}
                              >
                                <Text style={[s.calcPresetText, String(calcSacks || DEFAULT_SACKS) === String(v) && s.calcPresetTextActive]}>
                                  {v} Goni
                                </Text>
                              </TouchableOpacity>
                            ))}
                          </View>

                          {hasPendingWeighing && (
                            <Text style={s.calcPendingNote}>
                              Siap ditambah: {pendingNetKg.toLocaleString('id-ID')} kg • {pendingSacks} goni
                            </Text>
                          )}

                          <TouchableOpacity onPress={addWeighRow} style={[s.modalBtn, s.calcAddBtn]} activeOpacity={0.9}>
                            <Text style={s.modalBtnText}>+ Tambah</Text>
                          </TouchableOpacity>
                        </View>

                        {/* RIGHT: live running list, so results & mistakes show immediately without scrolling */}
                        <View style={s.calcListCol}>
                          <View style={[s.box, { padding: 8 }]}>
                            <View style={s.calcListHeaderRow}>
                              <Text style={s.boxTitle}>Daftar ({calcRows.length})</Text>
                              {calcRows.length > 0 && (
                                <Text style={s.calcListSummary}>{calcTotalNet.toLocaleString('id-ID')} kg</Text>
                              )}
                            </View>
                            {calcRows.length === 0 ? (
                              <View style={s.calcEmptyBox}>
                                <Text style={s.calcEmptyText}>Belum ada baris. Isi netto lalu tekan Tambah.</Text>
                              </View>
                            ) : (
                              // Capped to ~2 rows tall on purpose: the list should be a quick
                              // glance-and-check next to the input, not a tall panel — more
                              // rows just scroll inside this small window.
                              <ScrollView style={{ maxHeight: 112, marginTop: 4 }} nestedScrollEnabled showsVerticalScrollIndicator>
                                {calcRows.map((r, idx) => (
                                  <View key={r.id} style={s.rowMiniCompact}>
                                    <View style={{ flex: 1 }}>
                                      <Text style={s.calcRowTitle} numberOfLines={1}>#{idx + 1} • {Number(r.kg).toLocaleString('id-ID')} kg</Text>
                                      <Text style={s.calcRowSub}>{Number(r.sacks || 0).toLocaleString('id-ID')} goni</Text>
                                    </View>
                                    <TouchableOpacity
                                      onPress={()=>removeWeighRow(r.id)}
                                      style={s.badgeDelSmall}
                                      activeOpacity={0.9}
                                      hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
                                    >
                                      <Text style={s.badgeDelSmallText}>✕</Text>
                                    </TouchableOpacity>
                                  </View>
                                ))}
                              </ScrollView>
                            )}
                          </View>
                        </View>
                      </View>
                    </View>

                    <View style={s.calcSection}>
                      <Text style={s.calcSectionTitle}>2. Informasi Panen</Text>
                      <Text style={s.calcSectionHint}>Pilih lahan dan data transaksi utama.</Text>

                      <Text style={s.label}>Pilih Lahan</Text>
                      <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{ gap: 8, paddingRight: 8 }}>
                        {fields.map((f) => (
                          <TouchableOpacity
                            key={f.id}
                            style={[s.pill, String(calcFieldId) === String(f.id) && s.pillActive]}
                            onPress={() => {
                              setCalcFieldId(String(f.id));
                              setCalcPlotId(null);
                            }}
                            activeOpacity={0.9}
                          >
                            <Text style={[s.pillText, String(calcFieldId) === String(f.id) && s.pillTextActive]}>{f.name}</Text>
                          </TouchableOpacity>
                        ))}
                      </ScrollView>

                      <PlotPicker
                        fieldId={calcFieldId}
                        value={calcPlotId}
                        onChange={setCalcPlotId}
                        target="calc"
                      />


                      <View style={s.formGrid}>
                        <View style={{ flex: 1, minWidth: 150 }}>
                          <Text style={s.rowKey}>Tanggal</Text>
                          <TouchableOpacity
                            onPress={() => setShowCalcDatePicker((v) => !v)}
                            activeOpacity={0.8}
                            style={[s.input, s.inputLg, s.dateInputRow]}
                          >
                            <Text style={{ color: calcDate ? T.text : T.textMuted, fontWeight: '800' }}>
                              {calcDate || 'Pilih tanggal'}
                            </Text>
                            <Text style={s.dateInputIcon}>📅</Text>
                          </TouchableOpacity>
                        </View>
                        <View style={{ flex: 1, minWidth: 150 }}>
                          <Text style={s.rowKey}>Harga/kg (IDR)</Text>
                          <TextInput value={calcPrice} onChangeText={setCalcPrice} placeholder="0" keyboardType="number-pad" placeholderTextColor={T.textMuted} style={[s.input, s.inputLg]} />
                        </View>
                      </View>

                      {showCalcDatePicker && (
                        isWeb ? (
                          <View style={s.dateWebBox}>
                            <DatePicker
                              mode="single"
                              date={calcDateObj}
                              onChange={({ date: d }) => {
                                if (!d) return;
                                setCalcDate(toYMD(dayjs(d).toDate()));
                                setShowCalcDatePicker(false);
                              }}
                            />
                          </View>
                        ) : (
                          <DateTimePicker
                            value={calcDateObj}
                            mode="date"
                            display={Platform.OS === 'ios' ? 'spinner' : 'calendar'}
                            onChange={onNativeCalcDateChange}
                            maximumDate={new Date()}
                          />
                        )
                      )}

                      <View style={{ marginTop: 10 }}>
                        <Text style={s.rowKey}>Pembeli (opsional)</Text>
                        <TextInput value={buyerCalc} onChangeText={setBuyerCalc} placeholder="Nama pembeli" placeholderTextColor={T.textMuted} style={s.input} />
                        <Text style={[s.rowKey, { marginTop: 8 }]}>Catatan (opsional)</Text>
                        <TextInput value={notesCalc} onChangeText={setNotesCalc} placeholder="Catatan untuk bon" placeholderTextColor={T.textMuted} style={s.input} />
                      </View>

                      <EvidencePicker asset={calcEvidenceAsset} onChange={setCalcEvidenceAsset} />
                    </View>

                    <View style={s.calcSection}>
                      <Text style={s.calcSectionTitle}>3. Rekap</Text>
                      <View style={{ flexDirection:'row', flexWrap:'wrap', gap:10, marginTop:8 }}>
                        <Chip label="Total Netto" value={`${calcTotalNetProjected.toLocaleString('id-ID')} kg`} />
                        <Chip label="Total Goni" value={calcTotalSacksProjected} />
                        <Chip
                          label="Rata2/kg per Goni"
                          value={`${calcAvgNetPerSackProjected.toFixed(2)} kg`}
                        />
                        <Chip label="Pendapatan (est.)" value={formatIDR(calcRevenue)} strong />
                      </View>
                    </View>
                  </ScrollView>
                </View>

                <View style={s.calcFooter}>
                  <View style={s.calcFooterTopRow}>
                    <Text style={[s.calcFooterNote, { flex: 1 }]}>
                      {canSaveCalc
                        ? 'Simpan sekarang untuk membuat bon dan mencatat panen.'
                        : 'Lengkapi lahan dan minimal satu netto timbangan.'}
                    </Text>
                    <TouchableOpacity
                      onPress={confirmResetCalc}
                      style={s.calcResetLink}
                      activeOpacity={0.7}
                      hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
                    >
                      <Text style={s.calcResetLinkText}>Reset</Text>
                    </TouchableOpacity>
                  </View>
                  <View style={[s.modalRow, { marginTop: 6 }]}>
                    <TouchableOpacity style={[s.modalBtn, s.btnGhost]} onPress={()=>setShowCalc(false)} activeOpacity={0.9}>
                      <Text style={[s.modalBtnText, { color: T.text } ]}>Batal</Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                      style={[s.modalBtn, s.btnPrimary, (!canSaveCalc || savingCalc) && s.modalBtnDisabled]}
                      onPress={finishAndSaveWeighing}
                      disabled={savingCalc || !canSaveCalc}
                      activeOpacity={0.9}
                    >
                      <Text style={s.modalBtnText}>
                        {savingCalc ? 'Menyimpan…' : `Selesai & Simpan (${calcRows.length + (hasPendingWeighing ? 1 : 0)})`}
                      </Text>
                    </TouchableOpacity>
                  </View>
                </View>
              </View>
            </View>
            </KeyboardAvoidingView>
          </Modal>

          {/* VIEW WEIGHINGS MODAL */}
          <Modal visible={viewWeighModal} transparent animationType="fade" onRequestClose={() => setViewWeighModal(false)}>
            <View style={s.modalWrap}>
              <View style={[s.modalCard, { maxWidth: 720, width: '95%' }]}>
                <Text style={s.modalTitle}>Timbangan - ID Panen #{viewWeighHarvestId || '-'}</Text>

                <View style={{ maxHeight: 360 }}>
                  {viewWeighLoading ? (
                    <ActivityIndicator />
                  ) : viewWeighRows.length === 0 ? (
                    <Text style={{ color:T.textSoft, marginTop:6 }}>Belum ada data timbangan tersimpan.</Text>
                  ) : (
                    <ScrollView style={{ marginTop: 8 }}>
                      {viewWeighRows.map((r, i) => (
                        <View key={i} style={[s.rowMini, { justifyContent:'space-between' }]}>
                          <Text style={{ fontWeight:'900', color:T.text }}>{Number(r.kg).toLocaleString('id-ID')} kg</Text>
                          <Text style={{ color:T.textMuted, fontWeight:'800' }}>Goni {r.sacks}</Text>
                        </View>
                      ))}
                    </ScrollView>
                  )}
                </View>

                <View style={[s.modalRow, { marginTop: 12 }]}>
                  <TouchableOpacity style={[s.modalBtn, s.btnGhost]} onPress={()=>setViewWeighModal(false)} activeOpacity={0.9}>
                    <Text style={[s.modalBtnText, { color: T.text } ]}>Tutup</Text>
                  </TouchableOpacity>
                  <TouchableOpacity style={[s.modalBtn, s.btnPrimary]} onPress={()=>{
                    const url = `${API_BASE}/harvests/${viewWeighHarvestId}/receipt`;
                    if (Platform.OS === 'web') window.open(url, '_blank');
                    else Linking.openURL(url).catch(()=>Alert.alert('Error','Gagal membuka bon'));
                  }} activeOpacity={0.9}>
                    <Text style={s.modalBtnText}>Buka Bon (PDF)</Text>
                  </TouchableOpacity>
                </View>
              </View>
            </View>
          </Modal>

          {/* EDIT PRICE MODAL */}
          <Modal visible={editPriceModal} transparent animationType="fade" onRequestClose={() => setEditPriceModal(false)}>
            <View style={s.modalWrap}>
              <View style={[s.modalCard, { maxWidth: 520, width: '95%' }]}>
                <Text style={s.modalTitle}>Edit Harga/kg</Text>
                <Text style={s.sectionHint}>
                  {idToName[String(editPriceHarvest?.field_id)] || 'Lahan'} • {fmtDateShort(editPriceHarvest?.date)}
                </Text>

                <Text style={s.label}>Harga/kg (IDR)</Text>
                <TextInput
                  value={editPriceValue}
                  onChangeText={setEditPriceValue}
                  placeholder="0"
                  placeholderTextColor={T.textMuted}
                  keyboardType="numeric"
                  style={s.input}
                />

                <View style={s.modalRow}>
                  <TouchableOpacity style={[s.modalBtn, s.btnGhost]} onPress={() => setEditPriceModal(false)} activeOpacity={0.9}>
                    <Text style={[s.modalBtnText, { color: T.text }]}>Batal</Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={[s.modalBtn, s.btnPrimary, savingPrice && { opacity: 0.7 }]}
                    onPress={saveEditPrice}
                    disabled={savingPrice}
                    activeOpacity={0.9}
                  >
                    <Text style={s.modalBtnText}>{savingPrice ? 'Menyimpan…' : 'Simpan'}</Text>
                  </TouchableOpacity>
                </View>
              </View>
            </View>
          </Modal>

          {/* PAYMENT / DP MODAL */}
          <Modal visible={paymentModalOpen} transparent animationType="fade" onRequestClose={closePaymentModal}>
            <View style={s.modalWrap}>
              <View style={[s.modalCard, s.paymentModalCard, { maxWidth: 620, width: '95%' }]}>
                <View style={s.paymentModalHeader}>
                  <View style={{ flex: 1 }}>
                    <Text style={s.modalTitle}>DP Hasil Panen</Text>
                    <Text style={s.sectionHint}>
                      {idToName[String(paymentHarvest?.field_id)] || 'Lahan'} • {paymentHarvest?.buyer || 'Toke/tengkulak belum dicatat'}
                    </Text>
                  </View>
                  <TouchableOpacity style={s.paymentCloseBtn} onPress={closePaymentModal} disabled={!!paymentUpdatingId} activeOpacity={0.9}>
                    <Text style={s.paymentCloseText}>Tutup</Text>
                  </TouchableOpacity>
                </View>

                <ScrollView style={s.paymentModalScroll} contentContainerStyle={s.paymentModalContent} keyboardShouldPersistTaps="handled">
                  <View style={s.paymentPreview}>
                    <View style={s.paymentPreviewRow}>
                      <Text style={s.paymentPreviewKey}>Tagihan bersih</Text>
                      <Text style={s.paymentPreviewValue}>{formatIDR(paymentModalTotal)}</Text>
                    </View>
                    <View style={s.paymentPreviewRow}>
                      <Text style={s.paymentPreviewKey}>Sudah diterima</Text>
                      <Text style={[s.paymentPreviewValue, { color: T.brandGreen }]}>{formatIDR(paymentModalReceived)}</Text>
                    </View>
                    <View style={s.paymentPreviewRow}>
                      <Text style={s.paymentPreviewKey}>Sisa pembayaran</Text>
                      <Text style={[s.paymentPreviewValue, { color: paymentModalPending > 0 ? '#9A5A00' : T.brandGreen }]}>
                        {formatIDR(paymentModalPending)}
                      </Text>
                    </View>
                    <Text style={[s.sectionHintSoft, { marginTop: 7 }]}>Tagihan bersih sudah dikurangi biaya combine, sehingga tidak dihitung dua kali.</Text>
                  </View>

                  {paymentModalPending <= 0 && !editPaymentId ? (
                    <View style={s.paymentPaidNotice}>
                      <Text style={s.paymentPaidNoticeTitle}>Pembayaran sudah lunas</Text>
                      <Text style={s.paymentPaidNoticeText}>Anda masih bisa mengedit atau membatalkan transaksi pada riwayat di bawah.</Text>
                    </View>
                  ) : (
                    <View style={s.paymentFormCard}>
                      <View style={s.paymentFormTitleRow}>
                        <Text style={s.paymentFormTitle}>{editPaymentId ? 'Edit Pembayaran' : 'Tambah DP / Cicilan'}</Text>
                        {editPaymentId && (
                          <TouchableOpacity onPress={resetPaymentForm} style={s.paymentCancelEditBtn} activeOpacity={0.9}>
                            <Text style={s.paymentCancelEditText}>Batal edit</Text>
                          </TouchableOpacity>
                        )}
                      </View>

                      <Text style={s.label}>Nominal DP / cicilan diterima (IDR)</Text>
                      <TextInput
                        value={paymentAmount}
                        onChangeText={setPaymentAmount}
                        placeholder="contoh: 5000000"
                        placeholderTextColor={T.textMuted}
                        keyboardType="numeric"
                        style={[s.input, s.paymentAmountInput]}
                      />
                      <View style={s.paymentQuickRow}>
                        {[25, 50, 75].map((percent) => (
                          <TouchableOpacity
                            key={percent}
                            style={s.paymentQuickBtn}
                            onPress={() => setPaymentAmount(String(Math.round(paymentAvailableToAllocate * percent / 100)))}
                            activeOpacity={0.9}
                          >
                            <Text style={s.paymentQuickBtnText}>{percent}% sisa</Text>
                          </TouchableOpacity>
                        ))}
                        <TouchableOpacity
                          style={[s.paymentQuickBtn, s.paymentQuickBtnPrimary]}
                          onPress={() => setPaymentAmount(String(paymentAvailableToAllocate))}
                          activeOpacity={0.9}
                        >
                          <Text style={[s.paymentQuickBtnText, { color: T.brandGreen }]}>Isi sisa</Text>
                        </TouchableOpacity>
                      </View>

                      <View style={s.paymentTwoColumns}>
                        <View style={s.paymentColumn}>
                          <Text style={s.label}>Tanggal</Text>
                          <TextInput
                            value={paymentDate}
                            onChangeText={setPaymentDate}
                            placeholder="YYYY-MM-DD"
                            placeholderTextColor={T.textMuted}
                            style={s.input}
                            maxLength={10}
                          />
                        </View>
                        <View style={s.paymentColumn}>
                          <Text style={s.label}>Metode</Text>
                          <View style={s.paymentMethodRow}>
                            {[
                              { key: 'transfer', label: 'Transfer' },
                              { key: 'cash', label: 'Tunai' },
                              { key: 'deduction', label: 'Potongan' },
                              { key: 'other', label: 'Lainnya' },
                            ].map((option) => (
                              <TouchableOpacity
                                key={option.key}
                                style={[s.paymentMethodBtn, paymentMethod === option.key && s.paymentMethodBtnActive]}
                                onPress={() => setPaymentMethod(option.key)}
                                activeOpacity={0.9}
                              >
                                <Text style={[s.paymentMethodText, paymentMethod === option.key && s.paymentMethodTextActive]}>{option.label}</Text>
                              </TouchableOpacity>
                            ))}
                          </View>
                        </View>
                      </View>

                      <Text style={s.label}>Catatan (opsional)</Text>
                      <TextInput
                        value={paymentNote}
                        onChangeText={setPaymentNote}
                        placeholder="contoh: DP transfer dari Pak Budi"
                        placeholderTextColor={T.textMuted}
                        style={s.input}
                        maxLength={500}
                      />

                      {!!paymentAmount && (
                        <View style={s.paymentAfterSaveRow}>
                          <Text style={s.paymentAfterSaveKey}>Sisa setelah disimpan</Text>
                          <Text style={s.paymentAfterSaveValue}>{formatIDR(paymentPendingAfterSave)}</Text>
                        </View>
                      )}

                      <TouchableOpacity
                        style={[s.paymentSaveBtn, paymentUpdatingId && s.modalBtnDisabled]}
                        onPress={savePaymentAmount}
                        disabled={!!paymentUpdatingId}
                        activeOpacity={0.9}
                      >
                        {paymentUpdatingId ? <ActivityIndicator color="#fff" size="small" /> : (
                          <Text style={s.paymentSaveBtnText}>{editPaymentId ? 'Simpan Perubahan' : 'Simpan DP'}</Text>
                        )}
                      </TouchableOpacity>
                    </View>
                  )}

                  <View style={s.paymentHistoryHeader}>
                    <View style={{ flex: 1 }}>
                      <Text style={s.paymentHistoryTitle}>Riwayat Pembayaran</Text>
                      <Text style={s.paymentHistoryHint}>{paymentRows.length} transaksi aktif</Text>
                    </View>
                    {paymentRowsLoading && <ActivityIndicator color={T.brandGreen} size="small" />}
                  </View>

                  {!paymentRowsLoading && paymentRows.length === 0 ? (
                    <View style={s.paymentHistoryEmpty}>
                      <Text style={s.paymentHistoryEmptyText}>Belum ada DP atau pembayaran yang dicatat untuk hasil panen ini.</Text>
                    </View>
                  ) : paymentRows.map((row) => (
                    <View key={row.payment_id} style={[s.paymentHistoryItem, String(editPaymentId) === String(row.payment_id) && s.paymentHistoryItemActive]}>
                      <View style={s.paymentHistoryTop}>
                        <View style={{ flex: 1 }}>
                          <Text style={s.paymentHistoryAmount}>{formatIDR(row.amount)}</Text>
                          <Text style={s.paymentHistoryMeta}>
                            {fmtDateShort(row.payment_date)} • {{ transfer: 'Transfer', cash: 'Tunai', deduction: 'Potongan', other: 'Lainnya' }[row.method] || 'Lainnya'}
                          </Text>
                          {!!row.note && <Text style={s.paymentHistoryNote}>{row.note}</Text>}
                        </View>
                        <View style={s.paymentHistoryActions}>
                          <TouchableOpacity style={s.paymentHistoryEditBtn} onPress={() => editPaymentRow(row)} disabled={!!paymentUpdatingId} activeOpacity={0.9}>
                            <Text style={s.paymentHistoryEditText}>Edit</Text>
                          </TouchableOpacity>
                          <TouchableOpacity style={s.paymentHistoryVoidBtn} onPress={() => voidPaymentRow(row)} disabled={!!paymentUpdatingId} activeOpacity={0.9}>
                            <Text style={s.paymentHistoryVoidText}>Batalkan</Text>
                          </TouchableOpacity>
                        </View>
                      </View>
                    </View>
                  ))}
                </ScrollView>
              </View>
            </View>
          </Modal>

          {/* COMBINE HARVESTER COST MODAL */}
          <Modal visible={combineModalOpen} transparent animationType="fade" onRequestClose={() => setCombineModalOpen(false)}>
            <View style={s.modalWrap}>
              <View style={[s.modalCard, { maxWidth: 560, width: '95%' }]}>
                <Text style={s.modalTitle}>Biaya Odong-odong / Combine</Text>
                <Text style={s.sectionHint}>
                  {idToName[String(combineHarvest?.field_id)] || 'Lahan'} • {fmtDateShort(combineHarvest?.date)}
                </Text>

                <View style={s.paymentPreview}>
                  <View style={s.paymentPreviewRow}>
                    <Text style={s.paymentPreviewKey}>Pendapatan kotor</Text>
                    <Text style={s.paymentPreviewValue}>{formatIDR(combineModalTotal)}</Text>
                  </View>
                  <View style={s.paymentPreviewRow}>
                    <Text style={s.paymentPreviewKey}>Biaya combine per kg</Text>
                    <Text style={s.paymentPreviewValue}>
                      {formatIDR(combineModalKg > 0 ? combineDraftCost / combineModalKg : 0)}
                    </Text>
                  </View>
                  <View style={s.paymentPreviewRow}>
                    <Text style={s.paymentPreviewKey}>Hasil bersih</Text>
                    <Text style={[s.paymentPreviewValue, combineDraftNet < 0 && { color: T.dangerText }]}>
                      {formatIDR(combineDraftNet)}
                    </Text>
                  </View>
                </View>

                <Text style={s.label}>Total biaya Combine Harvester (IDR)</Text>
                <TextInput
                  value={combineCostValue}
                  onChangeText={setCombineCostValue}
                  placeholder="contoh: 2500000"
                  placeholderTextColor={T.textMuted}
                  keyboardType="numeric"
                  style={s.input}
                  autoFocus
                />
                <TouchableOpacity style={[s.paymentQuickBtn, { alignSelf: 'flex-start', marginTop: 9 }]} onPress={() => setCombineCostValue('0')} activeOpacity={0.9}>
                  <Text style={s.paymentQuickBtnText}>Reset biaya Rp0</Text>
                </TouchableOpacity>

                <Text style={s.label}>Nama operator / pemilik mesin (opsional)</Text>
                <TextInput
                  value={combineOperator}
                  onChangeText={setCombineOperator}
                  placeholder="contoh: Odong-odong Pak Budi"
                  placeholderTextColor={T.textMuted}
                  style={s.input}
                  maxLength={160}
                />

                <Text style={s.label}>Catatan (opsional)</Text>
                <TextInput
                  value={combineNote}
                  onChangeText={setCombineNote}
                  placeholder="contoh: biaya angkut sudah termasuk"
                  placeholderTextColor={T.textMuted}
                  style={[s.input, { minHeight: 72, textAlignVertical: 'top' }]}
                  multiline
                  maxLength={500}
                />
                <Text style={s.combineLinkedText}>
                  Disimpan sekali di sini dan otomatis masuk Pengeluaran › Panen › Combine Harvester.
                </Text>

                <View style={s.modalRow}>
                  <TouchableOpacity style={[s.modalBtn, s.btnGhost]} onPress={() => setCombineModalOpen(false)} activeOpacity={0.9}>
                    <Text style={[s.modalBtnText, { color: T.text }]}>Batal</Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={[s.modalBtn, s.btnPrimary, combineUpdatingId && { opacity: 0.65 }]}
                    onPress={saveCombineCost}
                    disabled={!!combineUpdatingId}
                    activeOpacity={0.9}
                  >
                    <Text style={s.modalBtnText}>{combineUpdatingId ? 'Menyimpan…' : 'Simpan Biaya'}</Text>
                  </TouchableOpacity>
                </View>
              </View>
            </View>
          </Modal>

          {/* ADD HARVEST PLOT MODAL */}
          <Modal visible={showAddPlot} transparent animationType="fade" onRequestClose={() => setShowAddPlot(false)}>
            <View style={s.modalWrap}>
              <View style={[s.modalCard, { maxWidth: 520, width: '95%' }]}>
                <Text style={s.modalTitle}>Tambah Petakan Panen</Text>
                <Text style={s.sectionHint}>
                  Lahan utama: {idToName[String(newPlotFieldId)] || 'Belum dipilih'}
                </Text>
                <Text style={s.label}>Nama Petakan</Text>
                <TextInput
                  value={newPlotName}
                  onChangeText={setNewPlotName}
                  placeholder="contoh: Petak A / Dekat Jalan"
                  placeholderTextColor={T.textMuted}
                  style={s.input}
                  autoFocus
                  maxLength={120}
                  onSubmitEditing={saveNewPlot}
                />
                <Text style={s.plotModalHint}>
                  Petakan hanya dipakai untuk tracking hasil panen. Operasional lahan utama tidak berubah.
                </Text>
                <View style={s.modalRow}>
                  <TouchableOpacity style={[s.modalBtn, s.btnGhost]} onPress={() => setShowAddPlot(false)} activeOpacity={0.9}>
                    <Text style={[s.modalBtnText, { color: T.text }]}>Batal</Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={[s.modalBtn, s.btnPrimary, savingPlot && { opacity: 0.65 }]}
                    onPress={saveNewPlot}
                    disabled={savingPlot}
                    activeOpacity={0.9}
                  >
                    <Text style={s.modalBtnText}>{savingPlot ? 'Menyimpan…' : 'Tambah & Pilih'}</Text>
                  </TouchableOpacity>
                </View>
              </View>
            </View>
          </Modal>

          {/* RECEIPT PHOTO VIEWER MODAL */}
          <Modal visible={receiptViewerOpen} transparent animationType="fade" onRequestClose={() => setReceiptViewerOpen(false)}>
            <View style={s.viewerWrap}>
              <View style={s.viewerTop}>
                <TouchableOpacity onPress={() => setReceiptViewerOpen(false)} style={s.viewerBtn} activeOpacity={0.9}>
                  <Text style={s.viewerBtnText}>Tutup</Text>
                </TouchableOpacity>
                {!!receiptViewerUrl && (
                  <TouchableOpacity
                    onPress={() => openBon(receiptViewerUrl)}
                    style={[s.viewerBtn, { backgroundColor: T.brandGreen }]}
                    activeOpacity={0.9}
                  >
                    <Text style={s.viewerBtnText}>Buka di Browser</Text>
                  </TouchableOpacity>
                )}
              </View>

              <View style={s.viewerCard}>
                {!!receiptViewerUrl ? (
                  <Image
                    source={{ uri: receiptViewerUrl }}
                    style={s.viewerImage}
                    resizeMode="contain"
                  />
                ) : (
                  <Text style={{ color: '#fff' }}>Tidak ada foto.</Text>
                )}
              </View>
            </View>
          </Modal>

          {/* Error */}
          {!!errorText && (
            <View style={s.alert}>
              <Text style={s.alertTitle}>Ada masalah</Text>
              <Text style={s.alertText} numberOfLines={4}>{errorText}</Text>
            </View>
          )}

          <View style={{ height: 24 }} />
        </View>
      </ScrollView>
    </View>
  );
}

const Chip = ({ label, value, strong=false }) => (
  <View style={s.chip}>
    <Text style={s.chipKey}>{label}</Text>
    <Text style={[s.chipVal, strong && { color:T.textGreen }]}>{String(value)}</Text>
  </View>
);

const Stat = ({ label, value, sub }) => (
  <View style={s.statBox}>
    <Text style={s.statLabel}>{label}</Text>
    <Text style={s.statValue}>{value}</Text>
    {!!sub && <Text style={s.statSub}>{sub}</Text>}
  </View>
);

const T = {
  bg: PADIPRO.bg,
  card: PADIPRO.surface,
  border: PADIPRO.border,
  borderSoft: PADIPRO.border,

  text: PADIPRO.text,
  textSoft: PADIPRO.muted,
  textMuted: PADIPRO.mutedSoft,
  textGreen: PADIPRO.primary,

  brandGreen: PADIPRO.primary,
  brandBlue: PADIPRO.sky,
  brandAmber: PADIPRO.gold,

  chipBg: PADIPRO.primarySoft,
  chipBorder: PADIPRO.border,

  rowBg: PADIPRO.surface,
  rowBorder: PADIPRO.border,

  dangerBg: PADIPRO.dangerSoft,
  dangerBorder: PADIPRO.dangerSoft,
  dangerText: PADIPRO.danger,

  linkBg: PADIPRO.skySoft,
  linkText: PADIPRO.sky,

  softBtnBg: PADIPRO.primarySoft,
};

const s = StyleSheet.create({
  screen: { flex: 1, backgroundColor: T.bg },

  bgTop: {
    position: 'absolute', top: -140, left: -120,
    width: 380, height: 380, borderRadius: 220,
    backgroundColor: PADIPRO.primarySoftStrong,
    opacity: 0.9
  },
  bgBottom: {
    position: 'absolute', bottom: -160, right: -120,
    width: 420, height: 420, borderRadius: 240,
    backgroundColor: PADIPRO.skySoft,
    opacity: 0.6
  },

  wrap: { padding: 16, paddingBottom: 28 },
  container: { width: '100%', alignSelf: 'center' },

  heroIconWrap: {
    width: 38,
    height: 38,
    borderRadius: 999,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255,255,255,0.16)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.38)',
  },
  heroActions: {
    marginTop: 10,
    flexDirection: 'row',
    gap: 8,
    flexWrap: 'wrap',
  },
  heroActionBtn: {
    minWidth: 145,
    flexGrow: 1,
    paddingVertical: 10,
    paddingHorizontal: 12,
    borderRadius: 12,
    borderWidth: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    ...padiproShadow,
  },
  heroActionPrimary: {
    backgroundColor: T.brandBlue,
    borderColor: T.brandBlue,
  },
  heroActionGhost: {
    backgroundColor: T.card,
    borderWidth: 1,
    borderColor: T.border,
  },
  heroActionTextPrimary: {
    color: '#fff',
    fontWeight: '900',
    fontSize: 13,
  },
  heroActionTextGhost: {
    color: T.text,
    fontWeight: '900',
    fontSize: 13,
  },

  card: {
    backgroundColor: T.card,
    borderWidth: 1,
    borderColor: T.border,
    borderRadius: 18,
    padding: 14,
    marginTop: 12,
    ...padiproShadow,
  },
  evidenceSuccess: {
    marginTop: 12,
    padding: 12,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: '#B7DFC0',
    backgroundColor: '#EAF7ED',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  evidenceSuccessTitle: { color: T.brandGreen, fontWeight: '900', fontSize: 13 },
  evidenceSuccessText: { color: T.textSoft, fontWeight: '700', fontSize: 11, marginTop: 2 },
  evidenceSuccessLink: { backgroundColor: T.brandGreen, paddingVertical: 8, paddingHorizontal: 11, borderRadius: 10 },
  evidenceSuccessLinkText: { color: '#fff', fontWeight: '900', fontSize: 11 },
  evidenceSuccessClose: { padding: 4 },
  evidenceSuccessCloseText: { color: T.textSoft, fontWeight: '900', fontSize: 20, lineHeight: 20 },

  sectionTitle: { color: T.text, fontWeight: '900', fontSize: 14 },
  sectionHint: { color: T.textSoft, fontWeight: '700', marginTop: 4 },
  sectionHintSoft: { color: T.textMuted, fontWeight: '700', marginTop: 4, fontSize: 12 },

  compactFilterTop: { flexDirection:'row', alignItems:'center', gap: 10 },

  groupTitle: { color: T.text, fontWeight: '900', fontSize: 12, marginBottom: 6 },
  pillsRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },

  inlineLoadingRow: { flexDirection: 'row', alignItems: 'center', gap: 8, paddingVertical: 8 },
  inlineLoadingText: { color: T.textSoft, fontWeight: '700' },

  seasonGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  seasonCard: {
    flexGrow: 1,
    minWidth: 220,
    backgroundColor: PADIPRO.surfaceSunken,
    borderWidth: 1,
    borderColor: T.border,
    borderRadius: 14,
    paddingVertical: 10,
    paddingHorizontal: 10,
  },
  seasonCardActive: {
    backgroundColor: T.brandGreen,
    borderColor: T.brandGreen,
  },
  seasonCardTop: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 8 },
  seasonCardTitle: { color: T.text, fontWeight: '900', flex: 1 },
  seasonCardTitleActive: { color: '#fff' },
  seasonCardSub: { color: T.textSoft, fontWeight: '700', marginTop: 6, fontSize: 12 },
  seasonCardSubActive: { color: PADIPRO.primarySoft },
  seasonBadge: {
    backgroundColor: PADIPRO.primarySoft,
    borderWidth: 1,
    borderColor: PADIPRO.primarySoftStrong,
    borderRadius: 999,
    paddingVertical: 2,
    paddingHorizontal: 8,
  },
  seasonBadgeActive: {
    backgroundColor: '#ffffff',
    borderColor: '#ffffff',
  },
  seasonBadgeText: { color: T.brandGreen, fontWeight: '900', fontSize: 11 },
  seasonBadgeTextActive: { color: T.brandGreen },
  emptySeasonBox: {
    backgroundColor: PADIPRO.surfaceSunken,
    borderWidth: 1,
    borderColor: T.border,
    borderRadius: 14,
    paddingVertical: 12,
    paddingHorizontal: 12,
  },
  emptySeasonText: { color: T.textSoft, fontWeight: '700' },
  backToActiveBtn: {
    marginTop: 10,
    paddingVertical: 8,
    paddingHorizontal: 10,
    borderRadius: 12,
    alignSelf: 'flex-start',
    backgroundColor: PADIPRO.primarySoft,
    borderWidth: 1,
    borderColor: PADIPRO.primarySoftStrong,
  },
  backToActiveText: { color: T.brandGreen, fontWeight: '900', fontSize: 12 },

  plotPickerBox: {
    marginTop: 10,
    padding: 11,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: PADIPRO.primarySoftStrong,
    backgroundColor: PADIPRO.primarySoft,
  },
  plotPickerHead: { flexDirection: 'row', alignItems: 'center', gap: 10 },
  plotPickerTitle: { color: T.text, fontWeight: '900', fontSize: 12.5 },
  plotPickerHint: { color: T.textSoft, fontWeight: '700', fontSize: 10.5, marginTop: 2 },
  plotAddBtn: {
    paddingHorizontal: 11,
    paddingVertical: 8,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: PADIPRO.primarySoftStrong,
    backgroundColor: '#fff',
  },
  plotAddBtnText: { color: T.brandGreen, fontWeight: '900', fontSize: 11 },
  plotPillsRow: { gap: 8, paddingTop: 10, paddingRight: 8 },
  plotEmptyText: { color: T.textMuted, fontWeight: '700', fontSize: 10.5, marginTop: 8 },
  plotTag: {
    alignSelf: 'flex-start',
    marginTop: 5,
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 999,
    backgroundColor: PADIPRO.primarySoft,
    borderWidth: 1,
    borderColor: PADIPRO.primarySoftStrong,
  },
  plotTagText: { color: T.brandGreen, fontWeight: '900', fontSize: 10.5 },
  plotModalHint: { color: T.textSoft, fontWeight: '700', fontSize: 11, marginTop: 8 },

  pill: {
    backgroundColor: T.softBtnBg,
    borderColor: T.border,
    borderWidth: 1,
    paddingVertical: 7,
    paddingHorizontal: 12,
    borderRadius: 999,
  },
  pillActive: { backgroundColor: T.brandGreen, borderColor: T.brandGreen },
  pillText: { color: T.text, fontWeight: '900' },
  pillTextActive: { color: '#fff' },

  summaryStrip: { flexDirection:'row', gap:10, flexWrap:'wrap', marginTop: 10 },
  statBox: { minWidth: 120, flexGrow: 1 },
  statLabel: { color:T.textSoft, fontWeight:'800' },
  statValue: { color:T.text, fontWeight:'900', fontSize:18, marginTop:2 },
  statSub:   { color:T.textSoft, marginTop:2, fontWeight: '700' },

  paymentSummaryRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 10, marginTop: 12 },
  paymentSummaryItem: { flexGrow: 1, flexBasis: 190, minWidth: 160, borderRadius: 14, borderWidth: 1, padding: 11 },
  paymentSummaryReceived: { backgroundColor: '#EAF7ED', borderColor: '#B7DFC0' },
  paymentSummaryPending: { backgroundColor: '#FFF8E8', borderColor: '#EED49A' },
  paymentSummaryLabel: { color: T.textSoft, fontWeight: '800', fontSize: 11 },
  paymentSummaryValue: { color: T.text, fontWeight: '900', fontSize: 16, marginTop: 3 },
  paymentSummarySub: { color: T.textSoft, fontWeight: '700', fontSize: 10.5, marginTop: 3 },
  paymentQuickRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginTop: 9 },
  paymentQuickBtn: { paddingVertical: 7, paddingHorizontal: 11, borderRadius: 999, borderWidth: 1, borderColor: T.border, backgroundColor: '#F6F7F2' },
  paymentQuickBtnText: { color: T.text, fontWeight: '900', fontSize: 11 },
  paymentPreview: { marginTop: 10, padding: 11, borderRadius: 12, backgroundColor: '#F4FAF5', borderWidth: 1, borderColor: '#D7E8D9' },
  paymentPreviewRow: { flexDirection: 'row', justifyContent: 'space-between', gap: 10, marginTop: 4 },
  paymentPreviewKey: { color: T.textSoft, fontWeight: '800', fontSize: 11 },
  paymentPreviewValue: { color: T.text, fontWeight: '900', fontSize: 12 },
  paymentModalCard: { maxHeight: '92%', paddingBottom: 0 },
  paymentModalHeader: { flexDirection: 'row', alignItems: 'flex-start', gap: 10 },
  paymentCloseBtn: { paddingVertical: 7, paddingHorizontal: 11, borderRadius: 10, backgroundColor: '#EEF2F7' },
  paymentCloseText: { color: T.text, fontWeight: '900', fontSize: 11 },
  paymentModalScroll: { flexShrink: 1 },
  paymentModalContent: { paddingBottom: 14 },
  paymentPaidNotice: { marginTop: 12, padding: 12, borderRadius: 12, backgroundColor: '#EAF7ED', borderWidth: 1, borderColor: '#B7DFC0' },
  paymentPaidNoticeTitle: { color: T.brandGreen, fontWeight: '900', fontSize: 13 },
  paymentPaidNoticeText: { color: T.textSoft, fontWeight: '700', fontSize: 11, marginTop: 3 },
  paymentFormCard: { marginTop: 12, padding: 12, borderRadius: 14, borderWidth: 1, borderColor: T.border, backgroundColor: '#FBFCF9' },
  paymentFormTitleRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 8 },
  paymentFormTitle: { color: T.text, fontWeight: '900', fontSize: 14 },
  paymentCancelEditBtn: { paddingVertical: 5, paddingHorizontal: 9, borderRadius: 9, backgroundColor: '#EEF2F7' },
  paymentCancelEditText: { color: T.textSoft, fontWeight: '900', fontSize: 10.5 },
  paymentAmountInput: { fontSize: 17 },
  paymentQuickBtnPrimary: { backgroundColor: PADIPRO.primarySoft, borderColor: PADIPRO.primarySoftStrong },
  paymentTwoColumns: { flexDirection: 'row', flexWrap: 'wrap', gap: 10 },
  paymentColumn: { flexGrow: 1, flexBasis: 210, minWidth: 180 },
  paymentMethodRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 6 },
  paymentMethodBtn: { paddingVertical: 10, paddingHorizontal: 10, borderRadius: 11, borderWidth: 1, borderColor: T.border, backgroundColor: '#fff' },
  paymentMethodBtnActive: { borderColor: T.brandGreen, backgroundColor: '#EAF7ED' },
  paymentMethodText: { color: T.textSoft, fontWeight: '900', fontSize: 10.5 },
  paymentMethodTextActive: { color: T.brandGreen },
  paymentAfterSaveRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginTop: 10, padding: 10, borderRadius: 10, backgroundColor: '#FFF8E8' },
  paymentAfterSaveKey: { color: T.textSoft, fontWeight: '800', fontSize: 11 },
  paymentAfterSaveValue: { color: '#9A5A00', fontWeight: '900', fontSize: 12 },
  paymentSaveBtn: { minHeight: 46, marginTop: 11, borderRadius: 13, alignItems: 'center', justifyContent: 'center', backgroundColor: T.brandGreen },
  paymentSaveBtnText: { color: '#fff', fontWeight: '900', fontSize: 13 },
  paymentHistoryHeader: { flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: 16, marginBottom: 8 },
  paymentHistoryTitle: { color: T.text, fontWeight: '900', fontSize: 14 },
  paymentHistoryHint: { color: T.textMuted, fontWeight: '700', fontSize: 10.5, marginTop: 2 },
  paymentHistoryEmpty: { padding: 14, borderRadius: 12, borderWidth: 1, borderStyle: 'dashed', borderColor: T.border, backgroundColor: '#FAFAF7' },
  paymentHistoryEmptyText: { color: T.textSoft, fontWeight: '700', fontSize: 11, textAlign: 'center' },
  paymentHistoryItem: { marginTop: 7, padding: 11, borderRadius: 12, borderWidth: 1, borderColor: T.border, backgroundColor: '#fff' },
  paymentHistoryItemActive: { borderColor: T.brandGreen, backgroundColor: '#F4FAF5' },
  paymentHistoryTop: { flexDirection: 'row', alignItems: 'flex-start', gap: 10 },
  paymentHistoryAmount: { color: T.text, fontWeight: '900', fontSize: 14 },
  paymentHistoryMeta: { color: T.textSoft, fontWeight: '800', fontSize: 10.5, marginTop: 2 },
  paymentHistoryNote: { color: T.textMuted, fontWeight: '700', fontSize: 10.5, marginTop: 4 },
  paymentHistoryActions: { flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'flex-end', gap: 5 },
  paymentHistoryEditBtn: { paddingVertical: 6, paddingHorizontal: 9, borderRadius: 9, backgroundColor: '#EAF7ED' },
  paymentHistoryEditText: { color: T.brandGreen, fontWeight: '900', fontSize: 10 },
  paymentHistoryVoidBtn: { paddingVertical: 6, paddingHorizontal: 9, borderRadius: 9, backgroundColor: T.dangerBg },
  paymentHistoryVoidText: { color: T.dangerText, fontWeight: '900', fontSize: 10 },

  plotSummaryGroup: {
    marginTop: 12,
    borderWidth: 1,
    borderColor: T.border,
    borderRadius: 14,
    overflow: 'hidden',
    backgroundColor: '#fff',
  },
  plotSummaryHead: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    padding: 11,
    backgroundColor: PADIPRO.primarySoft,
    borderBottomWidth: 1,
    borderBottomColor: PADIPRO.primarySoftStrong,
  },
  plotSummaryField: { color: T.text, fontWeight: '900', fontSize: 13 },
  plotSummaryMeta: { color: T.textSoft, fontWeight: '700', fontSize: 10.5, marginTop: 2 },
  plotSummaryTotalBox: { alignItems: 'flex-end' },
  plotSummaryTotalKey: { color: T.textSoft, fontWeight: '800', fontSize: 10 },
  plotSummaryTotalVal: { color: T.brandGreen, fontWeight: '900', fontSize: 13, marginTop: 2 },
  plotSummaryRow: { flexDirection: 'row', alignItems: 'center', gap: 10, padding: 11, borderBottomWidth: 1, borderBottomColor: '#EEF1EC' },
  plotRankBadge: { width: 24, height: 24, borderRadius: 12, alignItems: 'center', justifyContent: 'center', backgroundColor: T.brandGreen },
  plotRankText: { color: '#fff', fontWeight: '900', fontSize: 11 },
  plotSummaryRowTop: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 8 },
  plotSummaryName: { color: T.text, fontWeight: '900', fontSize: 12, flex: 1 },
  plotSummaryKg: { color: T.textGreen, fontWeight: '900', fontSize: 12 },
  plotProgressTrack: { height: 5, borderRadius: 999, backgroundColor: '#E5ECE4', overflow: 'hidden', marginTop: 6 },
  plotProgressFill: { height: '100%', borderRadius: 999, backgroundColor: T.brandGreen },
  plotSummarySub: { color: T.textMuted, fontWeight: '700', fontSize: 10, marginTop: 5 },
  plotSummaryEmpty: { marginTop: 10, padding: 11, borderRadius: 12, backgroundColor: PADIPRO.surfaceSunken },
  plotSummaryEmptyText: { color: T.textSoft, fontWeight: '700', fontSize: 11 },
  plotEmptyActions: { flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginTop: 10 },
  plotEmptyBtn: { backgroundColor: T.brandGreen, paddingVertical: 9, paddingHorizontal: 12, borderRadius: 10 },
  plotEmptyBtnText: { color: '#fff', fontWeight: '900', fontSize: 11 },
  plotEmptyBtnGhost: { backgroundColor: '#fff', borderWidth: 1, borderColor: T.border, paddingVertical: 9, paddingHorizontal: 12, borderRadius: 10 },
  plotEmptyBtnGhostText: { color: T.text, fontWeight: '900', fontSize: 11 },

  search: {
    marginTop: 10,
    backgroundColor:'#fff',
    borderWidth:1,
    borderColor: T.border,
    borderRadius: 14,
    paddingVertical: 11,
    paddingHorizontal: 12,
    color: T.text,
    fontWeight: '800'
  },

  row: {
    backgroundColor: T.rowBg,
    borderRadius: 18,
    borderWidth: 1,
    borderColor: T.rowBorder,
    padding: 12,
    marginTop: 10,
  },
  rowHeader: { flexDirection:'row', alignItems:'flex-start', justifyContent:'space-between', gap: 10 },
  rowTitle: { fontWeight:'900', color:T.text, fontSize: 14 },
  harvestLabel: {
    alignSelf: 'flex-start',
    marginTop: 4,
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 999,
    overflow: 'hidden',
    backgroundColor: PADIPRO.goldSoft,
    color: PADIPRO.gold,
    fontWeight: '900',
    fontSize: 11,
  },
  rowSub: { marginTop: 2, color: T.textSoft, fontWeight: '700', fontSize: 12 },

  rowActions: { flexDirection:'row', alignItems:'center', flexWrap:'wrap', gap: 6, justifyContent: 'flex-end' },

  rowCols: { flexDirection:'row', gap:10, flexWrap:'wrap', marginTop: 10 },
  rowCol: { minWidth: 120, flexGrow: 1 },
  rowKey: { color:T.textSoft, fontWeight:'800' },
  rowVal: { color:T.text, fontWeight:'900', marginTop: 4 },
  rowDim: { color:T.textMuted, fontWeight:'800' },
  combineLinkedText: { color: T.brandGreen, fontWeight: '800', fontSize: 10.5, marginTop: 3 },
  combineNetNegative: { color: T.dangerText },

  paymentBox: {
    marginTop: 11,
    padding: 11,
    borderRadius: 14,
    borderWidth: 1,
  },
  paymentBoxPaid: { backgroundColor: '#EAF7ED', borderColor: '#B7DFC0' },
  paymentBoxPartial: { backgroundColor: '#FFF8E8', borderColor: '#EED49A' },
  paymentBoxUnpaid: { backgroundColor: '#F7F7F3', borderColor: T.border },
  paymentHead: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  paymentDot: { width: 9, height: 9, borderRadius: 99 },
  paymentDotPaid: { backgroundColor: T.brandGreen },
  paymentDotPartial: { backgroundColor: '#C68114' },
  paymentDotUnpaid: { backgroundColor: T.textMuted },
  paymentStatus: { fontWeight: '900', fontSize: 12 },
  paymentStatusPaid: { color: T.brandGreen },
  paymentStatusPartial: { color: '#945C08' },
  paymentStatusUnpaid: { color: T.textSoft },
  paymentBuyer: { color: T.textSoft, fontWeight: '700', fontSize: 11, marginTop: 3 },
  paymentNumbers: { flexDirection: 'row', flexWrap: 'wrap', gap: 10, marginTop: 10 },
  paymentNumberItem: { flexGrow: 1, minWidth: 120 },
  paymentNumberKey: { color: T.textSoft, fontWeight: '800', fontSize: 10.5 },
  paymentNumberValue: { color: T.text, fontWeight: '900', fontSize: 13, marginTop: 2 },
  paymentPendingValue: { color: '#9A5A00' },
  paymentActions: { flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginTop: 10 },
  paymentActionBtn: { paddingVertical: 8, paddingHorizontal: 12, borderRadius: 10, backgroundColor: '#fff', borderWidth: 1, borderColor: T.border },
  paymentActionPrimary: { backgroundColor: T.brandGreen, borderColor: T.brandGreen },
  paymentActionText: { color: T.text, fontWeight: '900', fontSize: 11 },
  paymentActionPrimaryText: { color: '#fff' },

  badgeLink: {
    paddingVertical: 6,
    paddingHorizontal: 10,
    borderRadius: 999,
    backgroundColor: T.linkBg,
    borderWidth: 1,
    borderColor: 'rgba(37, 112, 143, 0.25)'
  },
  badgeLinkText: { fontSize: 12, fontWeight:'900', color: T.linkText },

  badgeLinkLight: {
    paddingVertical: 6,
    paddingHorizontal: 10,
    borderRadius: 999,
    backgroundColor: '#F6F7F2',
    borderWidth: 1,
    borderColor: T.border
  },
  badgeLinkLightText: { fontSize: 12, fontWeight:'900', color: T.text },

  editMiniBtn: {
    paddingVertical: 4,
    paddingHorizontal: 10,
    borderRadius: 999,
    backgroundColor: '#E7F3E9',
    borderWidth: 1,
    borderColor: '#CFE7D5'
  },
  editMiniBtnText: { fontSize: 12, fontWeight:'900', color: T.brandGreen },

  photoHintRow: { marginTop: 10, flexDirection:'row', alignItems:'center', gap: 8 },
  dotOk: { width: 8, height: 8, borderRadius: 99, backgroundColor: T.brandGreen },
  photoHintText: { color: T.textSoft, fontWeight: '800', fontSize: 12 },

  modalWrap: { flex:1, backgroundColor:'rgba(16,24,16,0.35)', justifyContent:'center', alignItems:'center', padding: 16 },
  modalCard: {
    width:'100%',
    backgroundColor:'#fff',
    borderRadius: 18,
    padding: 14,
    borderWidth: 1,
    borderColor: T.border,
  },
  modalTitle: { fontWeight:'900', color:T.text, fontSize:18, marginBottom: 8 },

  modalRow: { flexDirection:'row', gap: 8, marginTop: 12, flexWrap: 'wrap' },
  modalBtn: {
    flex: 1,
    minWidth: 140,
    borderRadius: 14,
    alignItems:'center',
    paddingVertical: 12,
    backgroundColor: T.brandBlue
  },
  modalBtnText: { color:'#fff', fontWeight:'900' },
  modalBtnDisabled: { opacity: 0.55 },

  btnPrimary: { backgroundColor: T.brandBlue },
  btnGhost: { backgroundColor: '#EEF2F7' },

  calcBody: { paddingBottom: 10 },
  calcIntroCard: {
    borderRadius: 14,
    borderWidth: 1,
    borderColor: '#D7E8D9',
    backgroundColor: '#F4FAF5',
    padding: 12,
    marginTop: 6,
  },
  calcIntroTitle: { color: T.text, fontWeight: '900', fontSize: 13 },
  calcIntroText: { color: T.textSoft, fontWeight: '700', fontSize: 12, marginTop: 4 },
  calcMetaRow: { flexDirection: 'row', gap: 8, flexWrap: 'wrap', marginTop: 10 },
  calcMetaChip: {
    flexGrow: 1,
    flexBasis: 96,
    paddingVertical: 8,
    paddingHorizontal: 10,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#D7E8D9',
    backgroundColor: '#ffffff',
  },
  calcMetaKey: { color: T.textSoft, fontWeight: '800', fontSize: 12 },
  calcMetaVal: { color: T.text, fontWeight: '900', marginTop: 2, fontSize: 13 },

  calcSection: {
    marginTop: 12,
    borderWidth: 1,
    borderColor: T.border,
    borderRadius: 14,
    backgroundColor: '#fff',
    padding: 12,
  },
  calcSectionTitle: { color: T.text, fontWeight: '900', fontSize: 13 },
  calcSectionHint: { color: T.textSoft, fontWeight: '700', fontSize: 12, marginTop: 4 },

  calcPresetRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginTop: 10 },
  calcPresetBtn: {
    minHeight: 40,
    justifyContent: 'center',
    paddingVertical: 10,
    paddingHorizontal: 14,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: T.border,
    backgroundColor: '#F5F8F4',
  },
  calcPresetBtnActive: { backgroundColor: '#DDF1E1', borderColor: '#B5DEBF' },
  calcPresetText: { color: T.textSoft, fontSize: 12, fontWeight: '800' },
  calcPresetTextActive: { color: T.brandGreen },
  calcPendingNote: { marginTop: 8, color: T.brandGreen, fontWeight: '800', fontSize: 12 },
  calcAddBtn: {
    backgroundColor: T.brandGreen,
    marginTop: 10,
    alignSelf: 'stretch',
    minHeight: 46,
    flex: undefined,
  },

  // Input pad (left) and running list (right) side by side, so a farmer
  // sees each weighing land in the list immediately — no scrolling down
  // to a separate section to check what was just entered.
  // Side by side only when there's genuinely room for it (tablet/desktop
  // web). On a real phone the columns would be too narrow to be usable,
  // so let them wrap back to stacked instead of forcing a squeezed
  // desktop-style layout onto small screens.
  calcInputListRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 12, marginTop: 10, alignItems: 'stretch' },
  calcInputCol: { flexGrow: 1, flexBasis: 170, minWidth: 160 },
  calcListCol: { flexGrow: 1, flexBasis: 170, minWidth: 160 },
  calcListHeaderRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  calcListSummary: { color: T.brandGreen, fontWeight: '900', fontSize: 12 },

  calcEmptyBox: {
    marginTop: 8,
    borderWidth: 1,
    borderColor: T.border,
    borderRadius: 12,
    padding: 12,
    backgroundColor: '#F7F6F1',
  },
  calcEmptyText: { color: T.textSoft, fontWeight: '700', fontSize: 12 },
  calcRowTitle: { color: T.text, fontWeight: '900', fontSize: 12 },
  calcRowSub: { color: T.textMuted, fontWeight: '800', fontSize: 11, marginTop: 2 },

  calcFooter: {
    marginTop: 10,
    borderTopWidth: 1,
    borderTopColor: T.border,
    paddingTop: 10,
  },
  calcFooterTopRow: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  calcFooterNote: { color: T.textSoft, fontWeight: '700', fontSize: 12 },
  calcResetLink: { paddingVertical: 4, paddingHorizontal: 4 },
  calcResetLinkText: { color: T.dangerText, fontWeight: '800', fontSize: 12, textDecorationLine: 'underline' },

  label: { fontWeight:'900', color:T.text, marginTop: 10, marginBottom: 6 },
  input: {
    backgroundColor:'#fff',
    borderWidth: 1,
    borderColor: T.border,
    borderRadius: 14,
    paddingVertical: 11,
    paddingHorizontal: 12,
    color: T.text,
    fontWeight: '800'
  },
  // Bigger tap/read target for the numbers a farmer types over and over
  // (netto kg, goni count) while standing in the field.
  inputLg: { paddingVertical: 14, fontSize: 16 },
  dateInputRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  dateInputIcon: { fontSize: 16 },
  dateWebBox: {
    marginTop: 8,
    backgroundColor: '#fff',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: T.border,
    overflow: 'hidden',
  },

  formGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: 10, marginTop: 6 },
  evidencePickerBox: {
    marginTop: 12,
    padding: 11,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: PADIPRO.primarySoftStrong,
    backgroundColor: PADIPRO.primarySoft,
  },
  evidencePickerHead: { flexDirection: 'row', alignItems: 'center', gap: 10 },
  evidencePickerTitle: { color: T.text, fontWeight: '900', fontSize: 12.5 },
  evidencePickerHint: { color: T.textSoft, fontWeight: '700', fontSize: 10.5, marginTop: 2 },
  evidencePickerBtn: {
    paddingHorizontal: 11,
    paddingVertical: 8,
    borderRadius: 10,
    backgroundColor: T.brandGreen,
    borderWidth: 1,
    borderColor: PADIPRO.primaryDark,
  },
  evidencePickerBtnText: { color: '#fff', fontWeight: '900', fontSize: 11 },
  evidencePreviewRow: {
    marginTop: 10,
    paddingTop: 10,
    borderTopWidth: 1,
    borderTopColor: PADIPRO.primarySoftStrong,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 9,
  },
  evidencePreviewImage: { width: 68, height: 54, borderRadius: 9, backgroundColor: PADIPRO.surfaceSunken },
  evidencePreviewName: { color: T.text, fontWeight: '900', fontSize: 11 },
  evidencePreviewReady: { color: T.brandGreen, fontWeight: '700', fontSize: 10, marginTop: 2 },
  evidenceRemoveBtn: { paddingHorizontal: 8, paddingVertical: 6 },
  evidenceRemoveText: { color: PADIPRO.danger, fontWeight: '900', fontSize: 10.5 },

  box: { borderWidth:1, borderColor: T.border, borderRadius: 14, padding: 12, backgroundColor:'#fff' },
  boxTitle: { fontWeight:'900', color:T.text },

  rowMini: {
    marginTop: 8,
    padding: 10,
    borderRadius: 14,
    backgroundColor: '#F7F6F1',
    borderWidth: 1,
    borderColor: T.border,
    flexDirection:'row',
    alignItems:'center',
    justifyContent:'space-between'
  },
  rowMiniCompact: {
    marginTop: 6,
    paddingVertical: 6,
    paddingHorizontal: 6,
    borderRadius: 12,
    backgroundColor: '#F7F6F1',
    borderWidth: 1,
    borderColor: T.border,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 4,
  },
  badgeDelSmall: {
    width: 26,
    height: 26,
    borderRadius: 999,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: PADIPRO.dangerSoft,
    borderWidth: 1,
    borderColor: 'rgba(159, 33, 56, 0.25)',
  },
  badgeDelSmallText: { fontSize: 12, fontWeight: '900', color: PADIPRO.danger },

  chip: {
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 14,
    backgroundColor: T.chipBg,
    borderWidth: 1,
    borderColor: T.chipBorder,
    minWidth: 160
  },
  chipKey: { color: T.textSoft, fontWeight:'800' },
  chipVal: { color: T.text, fontWeight:'900', marginTop: 4 },

  skelRow: { height: 92, borderRadius: 18, backgroundColor: PADIPRO.surfaceSunken, marginTop: 10, opacity: 0.7 },

  emptyBox: {
    alignItems: 'center',
    padding: 18,
    backgroundColor: T.card,
    borderWidth: 1,
    borderColor: T.border,
    borderRadius: 18,
  },
  emptyIcon: {
    width: 44, height: 44, borderRadius: 14,
    backgroundColor: '#E7F3E9',
    borderWidth: 1, borderColor: '#CFE7D5',
    alignItems: 'center', justifyContent: 'center'
  },
  emptyTitle: { marginTop: 10, color: T.text, fontWeight: '900' },
  emptyText: { color: T.textSoft, marginTop: 6, textAlign: 'center', fontWeight: '700' },

  alert: { backgroundColor: T.dangerBg, borderColor: T.dangerBorder, borderWidth: 1, padding: 12, borderRadius: 14, marginTop: 12 },
  alertTitle: { color: T.dangerText, fontWeight: '900', marginBottom: 4 },
  alertText: { color: T.dangerText, fontWeight: '700' },

  // viewer
  viewerWrap: { flex: 1, backgroundColor: 'rgba(0,0,0,0.86)', padding: 12, justifyContent:'center' },
  viewerTop: { flexDirection:'row', justifyContent:'space-between', alignItems:'center', marginBottom: 10, gap: 8 },
  viewerBtn: { paddingVertical: 10, paddingHorizontal: 12, borderRadius: 12, backgroundColor: PADIPRO.primaryDark },
  viewerBtnText: { color:'#fff', fontWeight:'900' },
  viewerCard: { backgroundColor: 'rgba(255,255,255,0.08)', borderRadius: 16, padding: 10, borderWidth: 1, borderColor: 'rgba(255,255,255,0.12)' },
  viewerImage: { width: '100%', height: 520, borderRadius: 12 },
});
